import {
  BoxGeometry,
  ConeGeometry,
  CylinderGeometry,
  IcosahedronGeometry
} from 'three';
import type { VariantPart } from './types';

/* ============================================================================
 * The Mayor's Mansion (Alpha 4.2)
 *
 * The most detailed single build in the game. 4 wide × 2 deep footprint.
 * The mansion sits along the back row (4 tiles wide × 1 tile deep); the
 * front row is the lavish formal estate grounds — central reflecting pool
 * with a 3-tiered marble fountain, parterre gardens flanking the pool,
 * paved drive leading to the front portico, bronze statues at the pool
 * corners, ornamental topiary cones along the perimeter, two ornamental
 * trees in the back corners, and a low stone balustrade ringing the lot.
 *
 * The mansion itself is a 5-section composition:
 *   - 2 outer wings (flanks, 2-storey, hipped roof)
 *   - 2 inner blocks (between wings + central, 2-storey, hipped roof)
 *   - 1 grand central block (3-storey, parapet, copper-green dome with
 *     spire + ball finial, pedimented portico with 6 columns + grand door
 *     + sweeping front steps)
 * Plus: 4 chimneys, ~30 individually-placed window panels with shutters,
 * a balustrade running the entire roofline, decorative cornice band
 * between stories, two flanking lampposts at the entrance, and an
 * ornamental garden urn at each front corner.
 *
 * Coordinate convention: anchor (ax, ay) is the lex-smallest tile of
 * the 4×2 footprint. The mansion footprint spans:
 *   x ∈ [ax,     ax+4)  (4 tiles wide along the X axis)
 *   z ∈ [ay,     ay+2)  (2 tiles deep along the Z axis)
 * Mansion body sits in z ∈ [ay, ay+1); grounds sit in z ∈ [ay+1, ay+2).
 *
 * Performance: ~140 BufferGeometry parts total. They merge into the same
 * `buildBuildingsMesh` (or `buildCityBuildingsMesh`) sweep that the rest
 * of the world uses, so the cost is one extra draw call's worth of
 * vertices — well within the per-frame budget on Pixel 7 / iPhone 13.
 * ========================================================================== */

/** Color palette — kept as constants up top so the whole composition
 *  reads with one consistent material story. */
const MM_LIMESTONE = 0xeae0c8;       // mansion walls, parapets
const MM_LIMESTONE_DEEP = 0xd4c8a8;  // pedestal, podium, deep cornice
const MM_LIMESTONE_DARK = 0xb8a684;  // step risers, pediment shadow
const MM_ROOF_SLATE = 0x4a5060;      // wing hipped roofs
const MM_ROOF_GABLE = 0x3a3e4a;      // pediment fascia
const MM_DOME_COPPER = 0x4f9f7a;     // weathered copper dome (matches existing clock_tower)
const MM_GOLD_TRIM = 0xeec453;       // lettering, finials
const MM_WINDOW_DARK = 0x1f2a3a;     // window glass
const MM_SHUTTER = 0x6e4622;         // dark wood shutters
const MM_DOOR_DARK = 0x3a2010;       // grand entrance door
const MM_LAWN = 0x4a8c3a;            // manicured estate lawn
const MM_LAWN_LIGHT = 0x5fa14a;      // parterre infill
const MM_HEDGE_DARK = 0x2d5e2a;      // formal hedge walls
const MM_HEDGE_BRIGHT = 0x3a7a3a;    // inner parterre cross
const MM_PAVING = 0xc7c0ad;          // driveway flagstone
const MM_PAVING_DEEP = 0xb0a895;     // pool surround
const MM_WATER = 0x4d8eb9;           // reflecting pool water
const MM_WATER_LIGHT = 0xc6dff0;     // pool surface highlight
const MM_BRONZE = 0x8c6a3a;          // statues
const MM_BRONZE_BRIGHT = 0xa07a44;   // statue heads
const MM_LAMPPOST = 0x222a32;        // wrought iron
const MM_LAMP_BULB = 0xf2cd5c;       // lamps
const MM_FLOWER_RED = 0xd84545;
const MM_FLOWER_YELLOW = 0xf2cd5c;
const MM_FLOWER_PURPLE = 0xa75ad4;
const MM_FLOWER_WHITE = 0xf6f0e0;
const MM_TREE_TRUNK = 0x6e3e1d;
const MM_TREE_LEAF = 0x2f6a2d;

export function buildMayorMansionParts(ax: number, ay: number): VariantPart[] {
  const out: VariantPart[] = [];
  // Footprint centre in world coords. TILE_SIZE = 1 so:
  //   width along X = 4 units, depth along Z = 2 units
  //   centre X = ax + 2; centre Z = ay + 1
  const cx = ax + 2;
  const cz = ay + 1;
  // Mansion body sits centred at z = cz - 0.5 (back-row centre).
  // Grounds sit centred at z = cz + 0.5 (front-row centre).
  const mz = cz - 0.5;
  const gz = cz + 0.5;

  // ===== ESTATE PAD (entire 4×2 footprint) =====
  // A subtle limestone-colored pad lifts the whole estate slightly off
  // the natural terrain. Reads as "this is a continuous, deliberate
  // composition" rather than a building dropped on grass.
  const estatePad = new BoxGeometry(3.95, 0.025, 1.95);
  estatePad.translate(cx, 0.0125, cz);
  out.push({ geom: estatePad, color: MM_LIMESTONE_DEEP });
  // Manicured lawn covers most of the front grounds + flanking borders.
  const frontLawn = new BoxGeometry(3.85, 0.020, 0.92);
  frontLawn.translate(cx, 0.024, gz);
  out.push({ geom: frontLawn, color: MM_LAWN });
  // ===== PERIMETER BALUSTRADE =====
  // Low stone wall around the entire footprint, broken at the front
  // centre by the main drive entrance.
  // Front (south) edge — left + right segments flanking the drive opening.
  const frontWallLeft = new BoxGeometry(1.45, 0.10, 0.06);
  frontWallLeft.translate(cx - 1.20, 0.07, cz + 0.97);
  out.push({ geom: frontWallLeft, color: MM_LIMESTONE });
  const frontWallRight = new BoxGeometry(1.45, 0.10, 0.06);
  frontWallRight.translate(cx + 1.20, 0.07, cz + 0.97);
  out.push({ geom: frontWallRight, color: MM_LIMESTONE });
  // Back (north) edge — solid, no break.
  const backWall = new BoxGeometry(3.95, 0.10, 0.06);
  backWall.translate(cx, 0.07, cz - 0.97);
  out.push({ geom: backWall, color: MM_LIMESTONE });
  // East + West edges.
  const eastWall = new BoxGeometry(0.06, 0.10, 1.95);
  eastWall.translate(cx + 1.97, 0.07, cz);
  out.push({ geom: eastWall, color: MM_LIMESTONE });
  const westWall = new BoxGeometry(0.06, 0.10, 1.95);
  westWall.translate(cx - 1.97, 0.07, cz);
  out.push({ geom: westWall, color: MM_LIMESTONE });
  // Corner posts — taller blocks with gold-finial caps.
  for (const [px, pz] of [[-1.97, -0.97], [1.97, -0.97], [-1.97, 0.97], [1.97, 0.97]] as Array<[number, number]>) {
    const post = new BoxGeometry(0.10, 0.20, 0.10);
    post.translate(cx + px, 0.10, cz + pz);
    out.push({ geom: post, color: MM_LIMESTONE });
    const finial = new ConeGeometry(0.05, 0.06, 6);
    finial.translate(cx + px, 0.23, cz + pz);
    out.push({ geom: finial, color: MM_GOLD_TRIM });
  }
  // Front gate posts (taller, flank the drive opening) with gold finials.
  for (const px of [-0.50, 0.50]) {
    const post = new BoxGeometry(0.10, 0.30, 0.10);
    post.translate(cx + px, 0.15, cz + 0.97);
    out.push({ geom: post, color: MM_LIMESTONE });
    const finial = new ConeGeometry(0.06, 0.08, 6);
    finial.translate(cx + px, 0.34, cz + 0.97);
    out.push({ geom: finial, color: MM_GOLD_TRIM });
  }

  // ===== GRAND DRIVE =====
  // Paved flagstone driveway running from the front gate to the steps
  // at the foot of the entrance portico.
  const drive = new BoxGeometry(1.00, 0.025, 0.95);
  drive.translate(cx, 0.027, gz);
  out.push({ geom: drive, color: MM_PAVING });
  // Drive border — slim limestone strip on each side.
  const driveBorderL = new BoxGeometry(0.06, 0.030, 0.95);
  driveBorderL.translate(cx - 0.50, 0.029, gz);
  out.push({ geom: driveBorderL, color: MM_LIMESTONE_DEEP });
  const driveBorderR = new BoxGeometry(0.06, 0.030, 0.95);
  driveBorderR.translate(cx + 0.50, 0.029, gz);
  out.push({ geom: driveBorderR, color: MM_LIMESTONE_DEEP });

  // ===== REFLECTING POOL with central FOUNTAIN =====
  // Two long reflecting pool sections flanking the central drive — they
  // run east-west between the drive border and the parterre garden.
  // (Monumental "pool by the driveway" composition.)
  for (const sideX of [-1.10, 1.10]) {
    // Pool surround.
    const surround = new BoxGeometry(1.05, 0.040, 0.50);
    surround.translate(cx + sideX, 0.035, gz);
    out.push({ geom: surround, color: MM_PAVING_DEEP });
    // Water inset.
    const water = new BoxGeometry(0.85, 0.030, 0.36);
    water.translate(cx + sideX, 0.045, gz);
    out.push({ geom: water, color: MM_WATER });
    // Subtle light reflection slab.
    const ripple = new BoxGeometry(0.30, 0.035, 0.05);
    ripple.translate(cx + sideX, 0.050, gz);
    out.push({ geom: ripple, color: MM_WATER_LIGHT });
    // Bronze statue at each end of the pool.
    for (const ex of [-0.42, 0.42]) {
      const plinth = new BoxGeometry(0.10, 0.10, 0.10);
      plinth.translate(cx + sideX + ex, 0.06, gz);
      out.push({ geom: plinth, color: MM_PAVING_DEEP });
      const statueLegs = new BoxGeometry(0.05, 0.10, 0.04);
      statueLegs.translate(cx + sideX + ex, 0.16, gz);
      out.push({ geom: statueLegs, color: MM_BRONZE });
      const statueTorso = new BoxGeometry(0.07, 0.08, 0.05);
      statueTorso.translate(cx + sideX + ex, 0.25, gz);
      out.push({ geom: statueTorso, color: MM_BRONZE });
      const statueHead = new BoxGeometry(0.04, 0.04, 0.04);
      statueHead.translate(cx + sideX + ex, 0.31, gz);
      out.push({ geom: statueHead, color: MM_BRONZE_BRIGHT });
    }
  }

  // ===== PARTERRE GARDENS (at the outermost columns of the front row) =====
  // Geometric formal hedges with flower-dot accents. One garden on each
  // outer corner of the front row.
  for (const sideX of [-1.65, 1.65]) {
    // Outer hedge frame — square box.
    const hedgeN = new BoxGeometry(0.60, 0.10, 0.04);
    hedgeN.translate(cx + sideX, 0.075, gz - 0.28);
    out.push({ geom: hedgeN, color: MM_HEDGE_DARK });
    const hedgeS = new BoxGeometry(0.60, 0.10, 0.04);
    hedgeS.translate(cx + sideX, 0.075, gz + 0.28);
    out.push({ geom: hedgeS, color: MM_HEDGE_DARK });
    const hedgeW = new BoxGeometry(0.04, 0.10, 0.60);
    hedgeW.translate(cx + sideX - 0.28, 0.075, gz);
    out.push({ geom: hedgeW, color: MM_HEDGE_DARK });
    const hedgeE = new BoxGeometry(0.04, 0.10, 0.60);
    hedgeE.translate(cx + sideX + 0.28, 0.075, gz);
    out.push({ geom: hedgeE, color: MM_HEDGE_DARK });
    // Inner cross hedge (forming 4 quadrants).
    const hCrossH = new BoxGeometry(0.50, 0.07, 0.03);
    hCrossH.translate(cx + sideX, 0.06, gz);
    out.push({ geom: hCrossH, color: MM_HEDGE_BRIGHT });
    const hCrossV = new BoxGeometry(0.03, 0.07, 0.50);
    hCrossV.translate(cx + sideX, 0.06, gz);
    out.push({ geom: hCrossV, color: MM_HEDGE_BRIGHT });
    // Flower dots in each of the four quadrants.
    const flowerColors = [MM_FLOWER_RED, MM_FLOWER_YELLOW, MM_FLOWER_PURPLE, MM_FLOWER_WHITE];
    let i = 0;
    for (const fz of [-0.13, 0.13]) {
      for (const fx of [-0.13, 0.13]) {
        const flower = new ConeGeometry(0.040, 0.06, 6);
        flower.translate(cx + sideX + fx, 0.10, gz + fz);
        out.push({ geom: flower, color: flowerColors[i++ % flowerColors.length]! });
      }
    }
    // Topiary corners — small cones at the four outer corners.
    for (const [tx, tz] of [[-0.30, -0.30], [0.30, -0.30], [-0.30, 0.30], [0.30, 0.30]] as Array<[number, number]>) {
      const top = new ConeGeometry(0.07, 0.18, 8);
      top.translate(cx + sideX + tx, 0.13, gz + tz);
      out.push({ geom: top, color: MM_HEDGE_BRIGHT });
    }
    // Lawn infill behind the hedge frame to brighten the parterre.
    const parterreLawn = new BoxGeometry(0.55, 0.025, 0.55);
    parterreLawn.translate(cx + sideX, 0.030, gz);
    out.push({ geom: parterreLawn, color: MM_LAWN_LIGHT });
  }

  // ===== ENTRANCE STEPS (foot of the portico, between drive + mansion) =====
  // Three-step grand stair leading up to the mansion porch level.
  for (let s = 0; s < 3; s++) {
    const step = new BoxGeometry(1.20 - s * 0.10, 0.045, 0.10);
    step.translate(cx, 0.045 + s * 0.045, mz + 0.45 - s * 0.05);
    out.push({ geom: step, color: MM_LIMESTONE_DARK });
  }
  // Two ornamental urns flanking the steps.
  for (const ux of [-0.65, 0.65]) {
    const urnBase = new CylinderGeometry(0.06, 0.05, 0.04, 8);
    urnBase.translate(cx + ux, 0.08, mz + 0.50);
    out.push({ geom: urnBase, color: MM_LIMESTONE_DEEP });
    const urnBowl = new CylinderGeometry(0.07, 0.06, 0.10, 8);
    urnBowl.translate(cx + ux, 0.15, mz + 0.50);
    out.push({ geom: urnBowl, color: MM_LIMESTONE });
    const urnTopiary = new ConeGeometry(0.07, 0.16, 8);
    urnTopiary.translate(cx + ux, 0.28, mz + 0.50);
    out.push({ geom: urnTopiary, color: MM_HEDGE_BRIGHT });
  }
  // Two wrought-iron lampposts flanking the steps further out.
  for (const lx of [-0.85, 0.85]) {
    const lampPole = new CylinderGeometry(0.018, 0.018, 0.46, 6);
    lampPole.translate(cx + lx, 0.27, mz + 0.50);
    out.push({ geom: lampPole, color: MM_LAMPPOST });
    const lampHead = new BoxGeometry(0.10, 0.10, 0.10);
    lampHead.translate(cx + lx, 0.55, mz + 0.50);
    out.push({ geom: lampHead, color: MM_LAMPPOST });
    const lampBulb = new BoxGeometry(0.06, 0.06, 0.06);
    lampBulb.translate(cx + lx, 0.55, mz + 0.50);
    out.push({ geom: lampBulb, color: MM_LAMP_BULB });
    const lampFinial = new ConeGeometry(0.025, 0.05, 6);
    lampFinial.translate(cx + lx, 0.625, mz + 0.50);
    out.push({ geom: lampFinial, color: MM_GOLD_TRIM });
  }

  // ===== MANSION BODY =====
  // The mansion runs along the back row, 4 tiles wide × 1 tile deep.
  // It's composed of 5 connected blocks: outer wings (×2), inner blocks
  // (×2), grand central (×1, taller).
  // Mansion footprint: width 3.40 (under the 4-unit estate width),
  // depth 0.65 (under the 1-unit back-row depth).
  const mansionDepth = 0.65;
  const mansionBackZ = mz - 0.10;  // body centre slightly behind centre of back row

  // Podium under the entire mansion — slim limestone slab.
  const mansionPodium = new BoxGeometry(3.50, 0.06, mansionDepth + 0.10);
  mansionPodium.translate(cx, 0.06, mansionBackZ);
  out.push({ geom: mansionPodium, color: MM_LIMESTONE_DEEP });

  // ----- OUTER WINGS (left + right, 2-storey, lower than centre) -----
  for (const wingX of [-1.30, 1.30]) {
    const wingW = 0.80;
    const wingH = 0.70;
    const wing = new BoxGeometry(wingW, wingH, mansionDepth);
    wing.translate(cx + wingX, 0.06 + wingH / 2 + 0.03, mansionBackZ);
    out.push({ geom: wing, color: MM_LIMESTONE });
    // Cornice band between the two stories.
    const cornice = new BoxGeometry(wingW + 0.02, 0.04, mansionDepth + 0.02);
    cornice.translate(cx + wingX, 0.06 + wingH * 0.5 + 0.03, mansionBackZ);
    out.push({ geom: cornice, color: MM_LIMESTONE_DEEP });
    // Hipped roof.
    const wingRoof = new ConeGeometry(0.55, 0.18, 4);
    wingRoof.rotateY(Math.PI / 4);
    wingRoof.translate(cx + wingX, 0.06 + wingH + 0.03 + 0.09, mansionBackZ);
    out.push({ geom: wingRoof, color: MM_ROOF_SLATE });
    // Roof balustrade — slim parapet running the wing's front edge.
    const wingBalustrade = new BoxGeometry(wingW + 0.04, 0.04, 0.04);
    wingBalustrade.translate(cx + wingX, 0.06 + wingH + 0.05, mansionBackZ + mansionDepth / 2 + 0.01);
    out.push({ geom: wingBalustrade, color: MM_LIMESTONE });
    // Twin chimneys per wing.
    for (const chx of [-0.20, 0.20]) {
      const chimney = new BoxGeometry(0.07, 0.12, 0.07);
      chimney.translate(cx + wingX + chx, 0.06 + wingH + 0.03 + 0.18, mansionBackZ - 0.10);
      out.push({ geom: chimney, color: MM_LIMESTONE_DARK });
      const chimneyCap = new BoxGeometry(0.09, 0.025, 0.09);
      chimneyCap.translate(cx + wingX + chx, 0.06 + wingH + 0.03 + 0.255, mansionBackZ - 0.10);
      out.push({ geom: chimneyCap, color: MM_ROOF_GABLE });
    }
    // Wing windows — 3 per story × 2 stories = 6 windows per wing.
    // Tall narrow rectangle with shutters on each side.
    for (let story = 0; story < 2; story++) {
      const yWindow = 0.06 + 0.18 + story * 0.32;
      for (const wxOff of [-0.25, 0.0, 0.25]) {
        // Glass.
        const win = new BoxGeometry(0.08, 0.16, 0.022);
        win.translate(cx + wingX + wxOff, yWindow, mansionBackZ + mansionDepth / 2 + 0.003);
        out.push({ geom: win, color: MM_WINDOW_DARK });
        // Left shutter.
        const sL = new BoxGeometry(0.045, 0.16, 0.018);
        sL.translate(cx + wingX + wxOff - 0.07, yWindow, mansionBackZ + mansionDepth / 2 + 0.005);
        out.push({ geom: sL, color: MM_SHUTTER });
        // Right shutter.
        const sR = new BoxGeometry(0.045, 0.16, 0.018);
        sR.translate(cx + wingX + wxOff + 0.07, yWindow, mansionBackZ + mansionDepth / 2 + 0.005);
        out.push({ geom: sR, color: MM_SHUTTER });
      }
    }
  }

  // ----- INNER BLOCKS (2-storey, between wings + central) -----
  for (const innerX of [-0.55, 0.55]) {
    const innerW = 0.55;
    const innerH = 0.85;  // slightly taller than wings
    const inner = new BoxGeometry(innerW, innerH, mansionDepth);
    inner.translate(cx + innerX, 0.06 + innerH / 2 + 0.03, mansionBackZ);
    out.push({ geom: inner, color: MM_LIMESTONE });
    // Cornice band.
    const cornice = new BoxGeometry(innerW + 0.02, 0.04, mansionDepth + 0.02);
    cornice.translate(cx + innerX, 0.06 + innerH * 0.5 + 0.03, mansionBackZ);
    out.push({ geom: cornice, color: MM_LIMESTONE_DEEP });
    // Hipped roof.
    const innerRoof = new ConeGeometry(0.40, 0.16, 4);
    innerRoof.rotateY(Math.PI / 4);
    innerRoof.translate(cx + innerX, 0.06 + innerH + 0.03 + 0.08, mansionBackZ);
    out.push({ geom: innerRoof, color: MM_ROOF_SLATE });
    // Inner-block windows — 2 per story × 2 stories = 4 each.
    for (let story = 0; story < 2; story++) {
      const yWindow = 0.06 + 0.22 + story * 0.38;
      for (const wxOff of [-0.13, 0.13]) {
        const win = new BoxGeometry(0.09, 0.20, 0.022);
        win.translate(cx + innerX + wxOff, yWindow, mansionBackZ + mansionDepth / 2 + 0.003);
        out.push({ geom: win, color: MM_WINDOW_DARK });
        const sL = new BoxGeometry(0.045, 0.20, 0.018);
        sL.translate(cx + innerX + wxOff - 0.075, yWindow, mansionBackZ + mansionDepth / 2 + 0.005);
        out.push({ geom: sL, color: MM_SHUTTER });
        const sR = new BoxGeometry(0.045, 0.20, 0.018);
        sR.translate(cx + innerX + wxOff + 0.075, yWindow, mansionBackZ + mansionDepth / 2 + 0.005);
        out.push({ geom: sR, color: MM_SHUTTER });
      }
    }
  }

  // ----- GRAND CENTRAL BLOCK (3-storey, parapet, dome, portico) -----
  const centralW = 0.95;
  const centralH = 1.10;
  const central = new BoxGeometry(centralW, centralH, mansionDepth + 0.10);
  central.translate(cx, 0.06 + centralH / 2 + 0.03, mansionBackZ);
  out.push({ geom: central, color: MM_LIMESTONE });
  // Two cornice bands (between three stories).
  for (const yC of [centralH / 3, centralH * 2 / 3]) {
    const cornice = new BoxGeometry(centralW + 0.03, 0.045, mansionDepth + 0.13);
    cornice.translate(cx, 0.06 + yC + 0.03, mansionBackZ);
    out.push({ geom: cornice, color: MM_LIMESTONE_DEEP });
  }
  // Roof parapet — wraps the top of the central block (no pitched roof,
  // it's a flat-top with a balustrade so the dome sits clean).
  const parapet = new BoxGeometry(centralW + 0.04, 0.06, mansionDepth + 0.14);
  parapet.translate(cx, 0.06 + centralH + 0.06, mansionBackZ);
  out.push({ geom: parapet, color: MM_LIMESTONE_DEEP });
  // Balustrade detail — a row of tiny posts along the parapet front.
  for (let i = 0; i < 11; i++) {
    const px = -centralW * 0.45 + i * (centralW * 0.9 / 10);
    const post = new BoxGeometry(0.022, 0.06, 0.022);
    post.translate(cx + px, 0.06 + centralH + 0.06, mansionBackZ + (mansionDepth + 0.14) / 2 + 0.005);
    out.push({ geom: post, color: MM_LIMESTONE });
  }
  // ===== PEDIMENT (triangular gable above the entrance, on the front) =====
  // Classical pediment built from 4 pieces:
  //   1. Horizontal entablature base (the architrave below the gable)
  //   2. Tympanum — flat triangular wall behind the gable, holds the
  //      gold escutcheon. Sits slightly recessed so the slopes cast a
  //      subtle shadow on it.
  //   3-4. Two angled slabs forming the triangular roof silhouette.
  // Pre-4.2.2 used a rotated 3-segment cone for the gable which produced
  // a wedge artifact protruding behind the central block ("weird black
  // box at the top"). The 4-piece composition is both cleaner visually
  // and structurally honest — the gable + tympanum + base read as a
  // proper Beaux-Arts pediment.
  const pedY = 0.06 + centralH * 0.85;       // base of the pediment
  const pedZ = mansionBackZ + mansionDepth / 2 + 0.05;
  // 1. Base entablature.
  const pedBase = new BoxGeometry(0.85, 0.04, 0.07);
  pedBase.translate(cx, pedY, pedZ + 0.05);
  out.push({ geom: pedBase, color: MM_LIMESTONE_DEEP });
  // 2. Tympanum (triangular wall behind the gable). Uses a 4-segment
  //    cone scaled thin in Z so it reads as a triangular slab from the
  //    front face. No rotation drama — the cone's natural apex-up
  //    orientation IS the pediment shape.
  const tympanum = new ConeGeometry(0.42, 0.20, 4);
  tympanum.rotateY(Math.PI / 4);   // align flat face to front
  tympanum.scale(1.0, 1.0, 0.18);  // squash in Z so it's a thin slab
  tympanum.translate(cx, pedY + 0.10, pedZ);
  out.push({ geom: tympanum, color: MM_LIMESTONE_DEEP });
  // 3-4. Two angled roof slabs forming the gable apex, in the darker
  //      slate-gable colour so the silhouette reads.
  const slopeLen = Math.hypot(0.40, 0.20);
  const slopeAngle = Math.atan2(0.20, 0.40);
  const leftSlope = new BoxGeometry(slopeLen, 0.04, 0.09);
  leftSlope.rotateZ(slopeAngle);
  leftSlope.translate(cx - 0.20, pedY + 0.10, pedZ + 0.005);
  out.push({ geom: leftSlope, color: MM_ROOF_GABLE });
  const rightSlope = new BoxGeometry(slopeLen, 0.04, 0.09);
  rightSlope.rotateZ(-slopeAngle);
  rightSlope.translate(cx + 0.20, pedY + 0.10, pedZ + 0.005);
  out.push({ geom: rightSlope, color: MM_ROOF_GABLE });
  // Gold escutcheon in the centre of the tympanum.
  const escutcheon = new BoxGeometry(0.10, 0.07, 0.022);
  escutcheon.translate(cx, pedY + 0.10, pedZ + 0.07);
  out.push({ geom: escutcheon, color: MM_GOLD_TRIM });

  // ===== PORTICO COLUMNS (6 columns supporting the pediment) =====
  // Tall slim cylinders running from podium to under the entablature.
  const colY = 0.06 + 0.45;
  const colH = 0.85;
  for (let i = 0; i < 6; i++) {
    const colX = -0.40 + i * (0.80 / 5);
    const col = new CylinderGeometry(0.025, 0.030, colH, 8);
    col.translate(cx + colX, colY, mansionBackZ + mansionDepth / 2 + 0.12);
    out.push({ geom: col, color: MM_LIMESTONE });
    // Capital (top of column).
    const cap = new BoxGeometry(0.08, 0.025, 0.08);
    cap.translate(cx + colX, colY + colH / 2 + 0.013, mansionBackZ + mansionDepth / 2 + 0.12);
    out.push({ geom: cap, color: MM_LIMESTONE_DEEP });
    // Base (bottom of column).
    const cBase = new BoxGeometry(0.08, 0.025, 0.08);
    cBase.translate(cx + colX, colY - colH / 2 - 0.013, mansionBackZ + mansionDepth / 2 + 0.12);
    out.push({ geom: cBase, color: MM_LIMESTONE_DEEP });
  }
  // Entablature above the columns (slim slab connecting their tops).
  const entablature = new BoxGeometry(0.95, 0.07, 0.08);
  entablature.translate(cx, colY + colH / 2 + 0.05, mansionBackZ + mansionDepth / 2 + 0.12);
  out.push({ geom: entablature, color: MM_LIMESTONE_DEEP });

  // ===== GRAND DOOR (centred under the portico) =====
  const door = new BoxGeometry(0.18, 0.40, 0.025);
  door.translate(cx, 0.06 + 0.22, mansionBackZ + mansionDepth / 2 + 0.005);
  out.push({ geom: door, color: MM_DOOR_DARK });
  // Door arch (rounded top — half cone).
  const doorArch = new ConeGeometry(0.10, 0.06, 12);
  doorArch.translate(cx, 0.06 + 0.45, mansionBackZ + mansionDepth / 2 + 0.005);
  out.push({ geom: doorArch, color: MM_LIMESTONE_DEEP });
  // Gold door handle.
  const handle = new BoxGeometry(0.020, 0.020, 0.030);
  handle.translate(cx + 0.06, 0.06 + 0.22, mansionBackZ + mansionDepth / 2 + 0.020);
  out.push({ geom: handle, color: MM_GOLD_TRIM });

  // ===== CENTRAL BLOCK WINDOWS (story 2 + 3, flanking the pediment) =====
  // Story 2 has 2 windows (one each side of the pediment area).
  // Story 3 has 4 windows in a row (above the pediment).
  for (const wxOff of [-0.34, 0.34]) {
    const win = new BoxGeometry(0.10, 0.22, 0.022);
    win.translate(cx + wxOff, 0.06 + 0.55, mansionBackZ + mansionDepth / 2 + 0.005);
    out.push({ geom: win, color: MM_WINDOW_DARK });
    const sL = new BoxGeometry(0.05, 0.22, 0.018);
    sL.translate(cx + wxOff - 0.085, 0.06 + 0.55, mansionBackZ + mansionDepth / 2 + 0.007);
    out.push({ geom: sL, color: MM_SHUTTER });
    const sR = new BoxGeometry(0.05, 0.22, 0.018);
    sR.translate(cx + wxOff + 0.085, 0.06 + 0.55, mansionBackZ + mansionDepth / 2 + 0.007);
    out.push({ geom: sR, color: MM_SHUTTER });
  }
  // Story 3 — top floor row of round-topped windows above pediment.
  for (const wxOff of [-0.30, -0.10, 0.10, 0.30]) {
    const win = new BoxGeometry(0.08, 0.14, 0.022);
    win.translate(cx + wxOff, 0.06 + 0.95, mansionBackZ + mansionDepth / 2 + 0.005);
    out.push({ geom: win, color: MM_WINDOW_DARK });
  }

  // ===== DOME on top of the central block =====
  // Drum (cylindrical base for the dome).
  const drum = new CylinderGeometry(0.20, 0.22, 0.10, 16);
  drum.translate(cx, 0.06 + centralH + 0.16, mansionBackZ);
  out.push({ geom: drum, color: MM_LIMESTONE });
  // Decorative columns around the drum.
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const px = Math.cos(angle) * 0.205;
    const pz = Math.sin(angle) * 0.205;
    const drumCol = new CylinderGeometry(0.012, 0.012, 0.10, 6);
    drumCol.translate(cx + px, 0.06 + centralH + 0.16, mansionBackZ + pz);
    out.push({ geom: drumCol, color: MM_LIMESTONE_DEEP });
  }
  // Dome itself — half-sphere via cone with many segments, topped by spire.
  const dome = new ConeGeometry(0.20, 0.30, 16);
  dome.translate(cx, 0.06 + centralH + 0.36, mansionBackZ);
  out.push({ geom: dome, color: MM_DOME_COPPER });
  // Lantern cupola at the top of the dome.
  const cupolaBase = new CylinderGeometry(0.05, 0.06, 0.08, 8);
  cupolaBase.translate(cx, 0.06 + centralH + 0.55, mansionBackZ);
  out.push({ geom: cupolaBase, color: MM_LIMESTONE });
  const cupolaTop = new ConeGeometry(0.06, 0.10, 8);
  cupolaTop.translate(cx, 0.06 + centralH + 0.64, mansionBackZ);
  out.push({ geom: cupolaTop, color: MM_DOME_COPPER });
  // Spire above the cupola.
  const spire = new CylinderGeometry(0.012, 0.012, 0.18, 5);
  spire.translate(cx, 0.06 + centralH + 0.78, mansionBackZ);
  out.push({ geom: spire, color: MM_GOLD_TRIM });
  // Gold ball finial on top.
  const ballFinial = new ConeGeometry(0.040, 0.07, 6);
  ballFinial.rotateX(Math.PI);
  ballFinial.translate(cx, 0.06 + centralH + 0.91, mansionBackZ);
  out.push({ geom: ballFinial, color: MM_GOLD_TRIM });

  // ===== ORNAMENTAL TREES in the back corners (behind wings) =====
  for (const tx of [-1.75, 1.75]) {
    // Shadow disc (Alpha 4.4) — slim dark-green octagonal pad under
    // the tree at the estate-pad surface. Matches the Alpha 2.6 polish
    // for forest tiles; extends shadow treatment to every tree-shaped
    // emission in the game.
    const shadow = new CylinderGeometry(0.34, 0.30, 0.005, 8);
    shadow.translate(cx + tx + 0.04, 0.030, mz - 0.27);
    out.push({ geom: shadow, color: 0x2a3a22 });
    const trunk = new CylinderGeometry(0.05, 0.06, 0.30, 6);
    trunk.translate(cx + tx, 0.18, mz - 0.30);
    out.push({ geom: trunk, color: MM_TREE_TRUNK });
    const foliage = new ConeGeometry(0.30, 0.65, 8);
    foliage.translate(cx + tx, 0.65, mz - 0.30);
    out.push({ geom: foliage, color: MM_TREE_LEAF });
    // Second smaller blob for a fuller crown.
    const blob = new ConeGeometry(0.20, 0.40, 8);
    blob.translate(cx + tx + 0.10, 0.85, mz - 0.20);
    out.push({ geom: blob, color: MM_TREE_LEAF });
  }

  return out;
}

/* ============================================================================
 * Civic monuments (Alpha 4.12)
 *
 * Three escalating one-per-city showpiece builds. Each is anchored at the
 * lex-smallest tile of its footprint (ax, ay) and emits the entire merged
 * geometry from that single dispatch site — same pattern as the Mayor's
 * Mansion. The visible footprint is sized so each step up reads as more
 * grand than the last:
 *
 *   City Hall            5 × 3   stately Beaux-Arts limestone, copper dome
 *   Provincial Capital   6 × 4   Queens Park (Toronto) influence, pink sandstone
 *   National Capital     7 × 4   Centre Block (Ottawa) influence, peace tower
 *
 * Each emits the building proper occupying the back ~1.2 tiles of the
 * footprint plus formal grounds (lawn, parterre, plaza, steps, statues,
 * lamposts) across the rest. Conventions match the mansion code:
 *   - World coords: TILE_SIZE = 1; centre X = ax + W/2; centre Z = ay + D/2
 *   - All geometries are translated into world position before push
 *   - Materials are flat-shaded via the global mergeGeoms pass (no normals)
 *   - Anchor (ax, ay) is the lex-smallest tile of the footprint
 * ========================================================================= */

// Shared palette — limestone + copper + bronze read as "civic monument" without
// needing a per-build palette. The provincial / national variants override
// the limestone tone (provincial = pink sandstone, national = grey Nepean
// sandstone) for visual identity.
const CH_LIMESTONE = 0xe8ddc6;
const CH_LIMESTONE_SHADOW = 0xb8a984;
const CH_LIMESTONE_DEEP = 0x8e7e60;
const CH_COPPER_GREEN = 0x4f9979;
const CH_COPPER_GREEN_DEEP = 0x3a7a5a;
const CH_GOLD = 0xd9b25a;
const CH_GOLD_BRIGHT = 0xeac96d;
const CH_DARK_WINDOW = 0x1e2632;
const CH_SLATE = 0x47525e;
const CH_LAWN = 0x4a7d3e;
const CH_PATH = 0xc9bfa1;
const CH_BRONZE = 0x8c6a3a;
const CH_LAMPPOST = 0x1c232b;
const CH_LAMP_BULB = 0xf2cd5c;
const CH_RED_FLAG = 0xc94343;
const CH_BLUE_FLAG = 0x3a5fa6;
const CH_HEDGE = 0x2f6a2d;
const CH_TREE_TRUNK = 0x6e3e1d;
const CH_TREE_LEAF = 0x2f6a2d;
// Provincial Capital — Queens Park's distinctive warm pink-orange sandstone.
const PC_SANDSTONE = 0xc89a7a;
const PC_SANDSTONE_SHADOW = 0x9c7458;
const PC_SANDSTONE_DEEP = 0x6e4d35;
const PC_SLATE_ROOF = 0x4a3a3a;
const PC_ROOF_DARK = 0x2f2422;
// National Capital — Centre Block's Nepean sandstone (cool grey-buff).
const NC_SANDSTONE = 0xbab09a;
const NC_SANDSTONE_SHADOW = 0x877e6a;
const NC_SANDSTONE_DEEP = 0x5a5448;
const NC_COPPER_ROOF = 0x4f9979;
const NC_COPPER_DEEP = 0x3a7a5a;
const NC_RED_DOOR = 0x8a3a2a;

/**
 * City Hall (Alpha 4.12). 5×3 footprint, Beaux-Arts composition.
 *
 * Layout (looking south, ax = west edge, ay = north edge):
 *
 *   ROW 0 (back, z = ay+0):  main building — 5 modules wide × ~0.7 deep
 *     [ W-PAVILION ] [ W-WING ] [ CENTRAL BLOCK + ROTUNDA + DOME ] [ E-WING ] [ E-PAVILION ]
 *
 *   ROW 1 (mid):             grand portico extending forward from centre,
 *                            flanking parterre gardens on both sides
 *
 *   ROW 2 (front):           ceremonial plaza + grand staircase down to
 *                            the street + flagpoles + fountain
 *
 * The user spec is explicit: more detail than the mansion, "modular"
 * meaning the building reads as five distinct modules (the SimCity 4 -
 * style civic composition pattern), with "bigger tiles" of geometry —
 * the central block and its dome are deliberately oversized for
 * monumental presence.
 */
export function buildCityHallParts(ax: number, ay: number): VariantPart[] {
  const out: VariantPart[] = [];
  // Footprint = 5 × 3. Centres:
  const cx = ax + 2.5;
  const cz = ay + 1.5;
  // Per-row centres:
  const bz = ay + 0.5;     // back-row (building) centre
  const mz = ay + 1.5;     // mid-row centre (portico + flanking parterres)
  const fz = ay + 2.5;     // front-row centre (ceremonial plaza)

  // ===== ESTATE PAD (entire 5×3 footprint) =====
  // Subtle limestone-tinted plinth that lifts the whole composition
  // off the natural terrain by 0.03. Marks the property line.
  const estatePad = new BoxGeometry(4.95, 0.025, 2.95);
  estatePad.translate(cx, 0.0125, cz);
  out.push({ geom: estatePad, color: CH_LIMESTONE_DEEP });
  // Front lawn rectangles flanking the ceremonial plaza.
  for (const dx of [-1.55, 1.55]) {
    const lawn = new BoxGeometry(1.75, 0.018, 1.85);
    lawn.translate(cx + dx, 0.024, fz - 0.05);
    out.push({ geom: lawn, color: CH_LAWN });
  }
  // Ceremonial central plaza — light flagstone strip from south edge
  // running to the grand stairs.
  const centralPath = new BoxGeometry(1.30, 0.022, 1.95);
  centralPath.translate(cx, 0.028, fz);
  out.push({ geom: centralPath, color: CH_PATH });
  // Flanking-parterre lawns either side of the portico (row 1).
  for (const dx of [-1.55, 1.55]) {
    const parterre = new BoxGeometry(1.75, 0.018, 0.92);
    parterre.translate(cx + dx, 0.024, mz - 0.03);
    out.push({ geom: parterre, color: CH_LAWN });
  }

  // ===== PERIMETER BALUSTRADE =====
  // Low stone wall around the whole footprint, broken at the front
  // centre by the ceremonial gate opening.
  const frontWallLeft = new BoxGeometry(1.85, 0.10, 0.06);
  frontWallLeft.translate(cx - 1.55, 0.07, cz + 1.47);
  out.push({ geom: frontWallLeft, color: CH_LIMESTONE });
  const frontWallRight = new BoxGeometry(1.85, 0.10, 0.06);
  frontWallRight.translate(cx + 1.55, 0.07, cz + 1.47);
  out.push({ geom: frontWallRight, color: CH_LIMESTONE });
  const backWall = new BoxGeometry(4.95, 0.10, 0.06);
  backWall.translate(cx, 0.07, cz - 1.47);
  out.push({ geom: backWall, color: CH_LIMESTONE });
  const eastWall = new BoxGeometry(0.06, 0.10, 2.95);
  eastWall.translate(cx + 2.47, 0.07, cz);
  out.push({ geom: eastWall, color: CH_LIMESTONE });
  const westWall = new BoxGeometry(0.06, 0.10, 2.95);
  westWall.translate(cx - 2.47, 0.07, cz);
  out.push({ geom: westWall, color: CH_LIMESTONE });
  // Six corner / gate posts with gold-finial caps.
  const cornerPosts: Array<[number, number]> = [
    [-2.47, -1.47], [2.47, -1.47],
    [-2.47,  1.47], [2.47,  1.47],
    [-0.65,  1.47], [0.65,  1.47]   // gate posts flanking the front opening
  ];
  for (const [px, pz] of cornerPosts) {
    const post = new BoxGeometry(0.10, 0.22, 0.10);
    post.translate(cx + px, 0.11, cz + pz);
    out.push({ geom: post, color: CH_LIMESTONE });
    const finial = new ConeGeometry(0.06, 0.10, 4);
    finial.translate(cx + px, 0.27, cz + pz);
    out.push({ geom: finial, color: CH_GOLD });
  }

  // ===== MAIN BUILDING (back row) =====
  // The "modular" composition — five distinct blocks read across the
  // facade: west pavilion → west wing → grand central block → east
  // wing → east pavilion.
  const buildingDepth = 0.80;
  const buildingZ = bz + 0.05;   // pushed slightly forward of back wall
  const buildingFrontZ = buildingZ + buildingDepth / 2;
  const baseY = 0.05;            // top of estate pad

  // --- WEST PAVILION (block 1) ---
  const wPavBody = new BoxGeometry(0.75, 0.85, buildingDepth);
  wPavBody.translate(cx - 1.85, baseY + 0.85 / 2, buildingZ);
  out.push({ geom: wPavBody, color: CH_LIMESTONE });
  // Pavilion mansard roof
  const wPavRoof = new BoxGeometry(0.85, 0.18, buildingDepth + 0.10);
  wPavRoof.translate(cx - 1.85, baseY + 0.85 + 0.09, buildingZ);
  out.push({ geom: wPavRoof, color: CH_SLATE });
  // Pavilion gabled top
  const wPavGable = new ConeGeometry(0.42, 0.18, 4);
  wPavGable.rotateY(Math.PI / 4);
  wPavGable.translate(cx - 1.85, baseY + 0.85 + 0.18 + 0.09, buildingZ);
  out.push({ geom: wPavGable, color: CH_SLATE });
  // Pavilion windows — 3 arched on the south face.
  for (const wxOff of [-0.18, 0, 0.18]) {
    for (let story = 0; story < 2; story++) {
      const win = new BoxGeometry(0.10, 0.18, 0.012);
      win.translate(cx - 1.85 + wxOff, baseY + 0.18 + story * 0.35, buildingFrontZ + 0.006);
      out.push({ geom: win, color: CH_DARK_WINDOW });
    }
  }

  // --- WEST WING (block 2) ---
  const wWingBody = new BoxGeometry(1.05, 0.75, buildingDepth);
  wWingBody.translate(cx - 1.05, baseY + 0.75 / 2, buildingZ);
  out.push({ geom: wWingBody, color: CH_LIMESTONE });
  // Wing roof — flat with cornice line.
  const wWingCornice = new BoxGeometry(1.12, 0.06, buildingDepth + 0.05);
  wWingCornice.translate(cx - 1.05, baseY + 0.75 + 0.03, buildingZ);
  out.push({ geom: wWingCornice, color: CH_LIMESTONE_SHADOW });
  // Arched windows across the wing — 4 tall arched openings.
  for (const wxOff of [-0.36, -0.12, 0.12, 0.36]) {
    // Pilaster between windows
    const pilaster = new BoxGeometry(0.05, 0.65, 0.025);
    pilaster.translate(cx - 1.05 + wxOff - 0.12, baseY + 0.36, buildingFrontZ + 0.013);
    out.push({ geom: pilaster, color: CH_LIMESTONE_SHADOW });
    // Window pane (arched top approximated by tall rectangle + tiny half-disc cap)
    const win = new BoxGeometry(0.12, 0.32, 0.012);
    win.translate(cx - 1.05 + wxOff, baseY + 0.36, buildingFrontZ + 0.007);
    out.push({ geom: win, color: CH_DARK_WINDOW });
    // Arched cap
    const arch = new CylinderGeometry(0.06, 0.06, 0.012, 8, 1, false, 0, Math.PI);
    arch.rotateX(Math.PI / 2);
    arch.rotateZ(-Math.PI / 2);
    arch.translate(cx - 1.05 + wxOff, baseY + 0.55, buildingFrontZ + 0.008);
    out.push({ geom: arch, color: CH_DARK_WINDOW });
  }

  // --- CENTRAL BLOCK (block 3) — the showpiece ---
  // Two storeys tall + rotunda + dome stack on top.
  const ccBlockH = 1.05;
  const ccBlock = new BoxGeometry(1.30, ccBlockH, buildingDepth);
  ccBlock.translate(cx, baseY + ccBlockH / 2, buildingZ);
  out.push({ geom: ccBlock, color: CH_LIMESTONE });
  // Entablature band running around the top of the central block.
  const entablature = new BoxGeometry(1.45, 0.08, buildingDepth + 0.08);
  entablature.translate(cx, baseY + ccBlockH + 0.04, buildingZ);
  out.push({ geom: entablature, color: CH_LIMESTONE_SHADOW });
  // Pediment over the front portico — classical triangular gable.
  const pediment = new ConeGeometry(0.55, 0.28, 3);
  pediment.rotateY(Math.PI / 2);
  pediment.scale(1.0, 1.0, 0.5);
  pediment.translate(cx, baseY + ccBlockH + 0.08 + 0.14, buildingFrontZ + 0.30);
  out.push({ geom: pediment, color: CH_LIMESTONE });
  // Gold escutcheon on the pediment
  const escutcheon = new BoxGeometry(0.20, 0.10, 0.020);
  escutcheon.translate(cx, baseY + ccBlockH + 0.10, buildingFrontZ + 0.32);
  out.push({ geom: escutcheon, color: CH_GOLD });

  // 6-column grand portico under the pediment
  const porticoZ = buildingFrontZ + 0.30;
  for (const colX of [-0.55, -0.33, -0.11, 0.11, 0.33, 0.55]) {
    // Column shaft
    const shaft = new CylinderGeometry(0.05, 0.055, 0.95, 8);
    shaft.translate(cx + colX, baseY + 0.475, porticoZ);
    out.push({ geom: shaft, color: CH_LIMESTONE });
    // Capital (Doric)
    const cap = new BoxGeometry(0.13, 0.05, 0.13);
    cap.translate(cx + colX, baseY + 0.95 + 0.025, porticoZ);
    out.push({ geom: cap, color: CH_LIMESTONE_SHADOW });
    // Base
    const cbase = new BoxGeometry(0.13, 0.04, 0.13);
    cbase.translate(cx + colX, baseY + 0.02, porticoZ);
    out.push({ geom: cbase, color: CH_LIMESTONE_SHADOW });
  }
  // Portico roof slab spanning the columns
  const porticoRoof = new BoxGeometry(1.30, 0.06, 0.18);
  porticoRoof.translate(cx, baseY + 1.00 + 0.03, porticoZ);
  out.push({ geom: porticoRoof, color: CH_LIMESTONE_SHADOW });

  // Grand arched main entrance behind the columns
  const doorBody = new BoxGeometry(0.32, 0.50, 0.025);
  doorBody.translate(cx, baseY + 0.27, buildingFrontZ + 0.013);
  out.push({ geom: doorBody, color: 0x4a2e1f });   // mahogany
  const doorArch = new CylinderGeometry(0.16, 0.16, 0.025, 10, 1, false, 0, Math.PI);
  doorArch.rotateX(Math.PI / 2);
  doorArch.rotateZ(-Math.PI / 2);
  doorArch.translate(cx, baseY + 0.55, buildingFrontZ + 0.013);
  out.push({ geom: doorArch, color: 0x4a2e1f });
  // Stone arch frame around the door
  const archFrame = new CylinderGeometry(0.20, 0.20, 0.018, 12, 1, false, 0, Math.PI);
  archFrame.rotateX(Math.PI / 2);
  archFrame.rotateZ(-Math.PI / 2);
  archFrame.translate(cx, baseY + 0.55, buildingFrontZ + 0.020);
  out.push({ geom: archFrame, color: CH_LIMESTONE_SHADOW });

  // ROTUNDA — circular drum sitting on the central block
  const rotundaH = 0.55;
  const rotunda = new CylinderGeometry(0.45, 0.45, rotundaH, 18);
  rotunda.translate(cx, baseY + ccBlockH + 0.08 + rotundaH / 2, buildingZ);
  out.push({ geom: rotunda, color: CH_LIMESTONE });
  // Rotunda colonnade — 12 small columns around the perimeter
  for (let i = 0; i < 12; i++) {
    const angle = (i / 12) * Math.PI * 2;
    const rcx = Math.cos(angle) * 0.42;
    const rcz = Math.sin(angle) * 0.42;
    const rcol = new CylinderGeometry(0.025, 0.025, rotundaH - 0.06, 6);
    rcol.translate(cx + rcx, baseY + ccBlockH + 0.08 + rotundaH / 2, buildingZ + rcz);
    out.push({ geom: rcol, color: CH_LIMESTONE_SHADOW });
  }
  // Rotunda entablature
  const rotundaCap = new CylinderGeometry(0.50, 0.50, 0.06, 18);
  rotundaCap.translate(cx, baseY + ccBlockH + 0.08 + rotundaH + 0.03, buildingZ);
  out.push({ geom: rotundaCap, color: CH_LIMESTONE_SHADOW });
  // DOME — copper-green hemisphere on the rotunda
  const dome = new CylinderGeometry(0.05, 0.45, 0.45, 18);  // truncated cone approximating dome
  dome.translate(cx, baseY + ccBlockH + 0.08 + rotundaH + 0.06 + 0.225, buildingZ);
  out.push({ geom: dome, color: CH_COPPER_GREEN });
  // Dome ribs — slim copper-deep meridians for definition
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const rib = new BoxGeometry(0.012, 0.46, 0.012);
    rib.translate(
      cx + Math.cos(angle) * 0.25,
      baseY + ccBlockH + 0.08 + rotundaH + 0.06 + 0.225,
      buildingZ + Math.sin(angle) * 0.25
    );
    rib.rotateY(angle);
    out.push({ geom: rib, color: CH_COPPER_GREEN_DEEP });
  }
  // Lantern atop the dome — small white-stone cupola with clock faces
  const lanternY = baseY + ccBlockH + 0.08 + rotundaH + 0.06 + 0.45;
  const lantern = new CylinderGeometry(0.10, 0.12, 0.18, 8);
  lantern.translate(cx, lanternY + 0.09, buildingZ);
  out.push({ geom: lantern, color: CH_LIMESTONE });
  // Four clock faces on the lantern (N/E/S/W)
  for (const [fx, fz] of [[0, 0.11], [0.11, 0], [0, -0.11], [-0.11, 0]] as Array<[number, number]>) {
    const clockFace = new CylinderGeometry(0.055, 0.055, 0.008, 12);
    clockFace.rotateX(Math.PI / 2);
    if (fx !== 0) clockFace.rotateY(Math.PI / 2);
    clockFace.translate(cx + fx, lanternY + 0.10, buildingZ + fz);
    out.push({ geom: clockFace, color: 0xf0e8d0 });
    // Hour hand
    const hourHand = new BoxGeometry(0.030, 0.006, 0.003);
    if (fx !== 0) hourHand.rotateY(Math.PI / 2);
    hourHand.translate(cx + fx, lanternY + 0.10, buildingZ + fz + (fz > 0 ? 0.005 : -0.005));
    out.push({ geom: hourHand, color: CH_DARK_WINDOW });
  }
  // Cupola roof — small copper cone
  const cupolaRoof = new ConeGeometry(0.13, 0.16, 8);
  cupolaRoof.translate(cx, lanternY + 0.18 + 0.08, buildingZ);
  out.push({ geom: cupolaRoof, color: CH_COPPER_GREEN_DEEP });
  // Gold ball finial
  const finialBall = new IcosahedronGeometry(0.04, 1);
  finialBall.translate(cx, lanternY + 0.30, buildingZ);
  out.push({ geom: finialBall, color: CH_GOLD_BRIGHT });
  // Flagpole
  const flagpole = new CylinderGeometry(0.008, 0.008, 0.35, 5);
  flagpole.translate(cx, lanternY + 0.46, buildingZ);
  out.push({ geom: flagpole, color: 0xb8b8b8 });
  // Flag
  const flag = new BoxGeometry(0.16, 0.10, 0.005);
  flag.translate(cx + 0.08, lanternY + 0.56, buildingZ);
  out.push({ geom: flag, color: CH_RED_FLAG });

  // --- EAST WING (block 4) — mirror of west wing ---
  const eWingBody = new BoxGeometry(1.05, 0.75, buildingDepth);
  eWingBody.translate(cx + 1.05, baseY + 0.75 / 2, buildingZ);
  out.push({ geom: eWingBody, color: CH_LIMESTONE });
  const eWingCornice = new BoxGeometry(1.12, 0.06, buildingDepth + 0.05);
  eWingCornice.translate(cx + 1.05, baseY + 0.75 + 0.03, buildingZ);
  out.push({ geom: eWingCornice, color: CH_LIMESTONE_SHADOW });
  for (const wxOff of [-0.36, -0.12, 0.12, 0.36]) {
    const pilaster = new BoxGeometry(0.05, 0.65, 0.025);
    pilaster.translate(cx + 1.05 + wxOff - 0.12, baseY + 0.36, buildingFrontZ + 0.013);
    out.push({ geom: pilaster, color: CH_LIMESTONE_SHADOW });
    const win = new BoxGeometry(0.12, 0.32, 0.012);
    win.translate(cx + 1.05 + wxOff, baseY + 0.36, buildingFrontZ + 0.007);
    out.push({ geom: win, color: CH_DARK_WINDOW });
    const arch = new CylinderGeometry(0.06, 0.06, 0.012, 8, 1, false, 0, Math.PI);
    arch.rotateX(Math.PI / 2);
    arch.rotateZ(-Math.PI / 2);
    arch.translate(cx + 1.05 + wxOff, baseY + 0.55, buildingFrontZ + 0.008);
    out.push({ geom: arch, color: CH_DARK_WINDOW });
  }

  // --- EAST PAVILION (block 5) — mirror of west pavilion ---
  const ePavBody = new BoxGeometry(0.75, 0.85, buildingDepth);
  ePavBody.translate(cx + 1.85, baseY + 0.85 / 2, buildingZ);
  out.push({ geom: ePavBody, color: CH_LIMESTONE });
  const ePavRoof = new BoxGeometry(0.85, 0.18, buildingDepth + 0.10);
  ePavRoof.translate(cx + 1.85, baseY + 0.85 + 0.09, buildingZ);
  out.push({ geom: ePavRoof, color: CH_SLATE });
  const ePavGable = new ConeGeometry(0.42, 0.18, 4);
  ePavGable.rotateY(Math.PI / 4);
  ePavGable.translate(cx + 1.85, baseY + 0.85 + 0.18 + 0.09, buildingZ);
  out.push({ geom: ePavGable, color: CH_SLATE });
  for (const wxOff of [-0.18, 0, 0.18]) {
    for (let story = 0; story < 2; story++) {
      const win = new BoxGeometry(0.10, 0.18, 0.012);
      win.translate(cx + 1.85 + wxOff, baseY + 0.18 + story * 0.35, buildingFrontZ + 0.006);
      out.push({ geom: win, color: CH_DARK_WINDOW });
    }
  }

  // ===== GRAND STAIRCASE (middle row, leading from portico to plaza) =====
  // Four wide steps cascading down. Each step is wider than the last
  // visually, but here we render as a stack of slightly receding boxes.
  for (let i = 0; i < 4; i++) {
    const stepW = 1.30 + i * 0.15;
    const stepZ = mz - 0.55 + i * 0.18;
    const stepY = baseY - i * 0.012;
    const step = new BoxGeometry(stepW, 0.05, 0.18);
    step.translate(cx, Math.max(0.035, stepY + 0.025), stepZ);
    out.push({ geom: step, color: CH_LIMESTONE_SHADOW });
  }
  // Stair flanking railings
  for (const dx of [-0.85, 0.85]) {
    const rail = new BoxGeometry(0.08, 0.30, 0.72);
    rail.translate(cx + dx, baseY + 0.15, mz - 0.20);
    out.push({ geom: rail, color: CH_LIMESTONE });
    // Cap finial
    const railCap = new ConeGeometry(0.05, 0.08, 4);
    railCap.translate(cx + dx, baseY + 0.34, mz - 0.20);
    out.push({ geom: railCap, color: CH_GOLD });
  }

  // ===== FLANKING PARTERRE GARDENS (mid row) =====
  // Two formal gardens flanking the portico: each is a 1.5×0.85 lawn pad
  // with a hedge cross + flower-spot quadrants + topiary cone corners.
  for (const dx of [-1.55, 1.55]) {
    // Hedge cross
    const hedgeNS = new BoxGeometry(0.08, 0.10, 0.80);
    hedgeNS.translate(cx + dx, baseY + 0.07, mz - 0.03);
    out.push({ geom: hedgeNS, color: CH_HEDGE });
    const hedgeEW = new BoxGeometry(0.90, 0.10, 0.08);
    hedgeEW.translate(cx + dx, baseY + 0.07, mz - 0.03);
    out.push({ geom: hedgeEW, color: CH_HEDGE });
    // Topiary cones at each corner of the parterre
    for (const [qx, qz] of [[-0.55, -0.30], [0.55, -0.30], [-0.55, 0.30], [0.55, 0.30]] as Array<[number, number]>) {
      const topiary = new ConeGeometry(0.10, 0.22, 6);
      topiary.translate(cx + dx + qx, baseY + 0.11, mz - 0.03 + qz);
      out.push({ geom: topiary, color: CH_HEDGE });
    }
    // Flower-spot quadrants
    for (const [fx, fz] of [[-0.25, -0.18], [0.25, -0.18], [-0.25, 0.18], [0.25, 0.18]] as Array<[number, number]>) {
      const flower = new BoxGeometry(0.18, 0.022, 0.18);
      flower.translate(cx + dx + fx, baseY + 0.012, mz - 0.03 + fz);
      out.push({ geom: flower, color: 0xd84545 });
    }
  }

  // ===== FRONT FOUNTAIN (front row centre) =====
  // Circular limestone basin with central plinth and gold jet.
  const fountainBasin = new CylinderGeometry(0.32, 0.32, 0.10, 18);
  fountainBasin.translate(cx, baseY + 0.05, fz - 0.20);
  out.push({ geom: fountainBasin, color: CH_LIMESTONE });
  // Basin water
  const fountainWater = new CylinderGeometry(0.28, 0.28, 0.02, 18);
  fountainWater.translate(cx, baseY + 0.10, fz - 0.20);
  out.push({ geom: fountainWater, color: 0x6da8d6 });
  // Central plinth
  const fountainPlinth = new BoxGeometry(0.14, 0.20, 0.14);
  fountainPlinth.translate(cx, baseY + 0.20, fz - 0.20);
  out.push({ geom: fountainPlinth, color: CH_LIMESTONE_SHADOW });
  // Jet (gold cone, pointing up)
  const fountainJet = new ConeGeometry(0.05, 0.22, 8);
  fountainJet.translate(cx, baseY + 0.41, fz - 0.20);
  out.push({ geom: fountainJet, color: CH_GOLD_BRIGHT });

  // ===== FLANKING FLAGPOLES (front of staircase) =====
  for (const dx of [-1.10, 1.10]) {
    // Granite base
    const flagBase = new BoxGeometry(0.20, 0.10, 0.20);
    flagBase.translate(cx + dx, baseY + 0.05, fz - 0.55);
    out.push({ geom: flagBase, color: CH_LIMESTONE_SHADOW });
    // Pole
    const pole = new CylinderGeometry(0.018, 0.020, 0.85, 6);
    pole.translate(cx + dx, baseY + 0.52, fz - 0.55);
    out.push({ geom: pole, color: 0xb8b8b8 });
    // Flag
    const flagF = new BoxGeometry(0.22, 0.14, 0.005);
    flagF.translate(cx + dx + 0.11, baseY + 0.85, fz - 0.55);
    out.push({ geom: flagF, color: dx < 0 ? CH_RED_FLAG : CH_BLUE_FLAG });
    // Gold ball at pole top
    const poleBall = new IcosahedronGeometry(0.022, 1);
    poleBall.translate(cx + dx, baseY + 0.96, fz - 0.55);
    out.push({ geom: poleBall, color: CH_GOLD });
  }

  // ===== ORNAMENTAL LAMPPOSTS (six along the perimeter walls) =====
  const lampSpots: Array<[number, number]> = [
    [-2.25, -0.50], [2.25, -0.50],
    [-2.25,  0.70], [2.25,  0.70],
    [-2.25,  1.30], [2.25,  1.30]
  ];
  for (const [px, pz] of lampSpots) {
    // Cast-iron post
    const lampPost = new CylinderGeometry(0.020, 0.025, 0.45, 6);
    lampPost.translate(cx + px, baseY + 0.225, cz + pz);
    out.push({ geom: lampPost, color: CH_LAMPPOST });
    // Lamp housing
    const lampHouse = new BoxGeometry(0.08, 0.08, 0.08);
    lampHouse.translate(cx + px, baseY + 0.49, cz + pz);
    out.push({ geom: lampHouse, color: CH_LAMPPOST });
    // Bulb
    const bulb = new IcosahedronGeometry(0.035, 1);
    bulb.translate(cx + px, baseY + 0.49, cz + pz);
    out.push({ geom: bulb, color: CH_LAMP_BULB });
  }

  // ===== BRONZE STATUES (two on plinths flanking the central plaza) =====
  for (const dx of [-0.70, 0.70]) {
    // Granite plinth
    const sPlinth = new BoxGeometry(0.18, 0.20, 0.18);
    sPlinth.translate(cx + dx, baseY + 0.10, fz + 0.20);
    out.push({ geom: sPlinth, color: CH_LIMESTONE_SHADOW });
    // Bronze figure (simplified — torso + head, no limbs)
    const sBody = new BoxGeometry(0.06, 0.16, 0.05);
    sBody.translate(cx + dx, baseY + 0.28, fz + 0.20);
    out.push({ geom: sBody, color: CH_BRONZE });
    const sHead = new IcosahedronGeometry(0.030, 1);
    sHead.translate(cx + dx, baseY + 0.38, fz + 0.20);
    out.push({ geom: sHead, color: CH_BRONZE });
  }

  // ===== ORNAMENTAL TREES IN BACK CORNERS =====
  // Behind the building (back row, behind the pavilions).
  for (const tx of [-2.20, 2.20]) {
    const shadow = new CylinderGeometry(0.32, 0.28, 0.005, 8);
    shadow.translate(cx + tx + 0.04, 0.030, bz - 0.40);
    out.push({ geom: shadow, color: 0x2a3a22 });
    const trunk = new CylinderGeometry(0.05, 0.06, 0.30, 6);
    trunk.translate(cx + tx, 0.18, bz - 0.40);
    out.push({ geom: trunk, color: CH_TREE_TRUNK });
    const foliage = new ConeGeometry(0.28, 0.60, 8);
    foliage.translate(cx + tx, 0.62, bz - 0.40);
    out.push({ geom: foliage, color: CH_TREE_LEAF });
  }

  return out;
}

/**
 * Provincial Capital (Alpha 4.12). 6×4 footprint, Queens Park influence —
 * Romanesque Revival in pink-orange sandstone, distinctive central
 * arched main entrance, low central tower with copper cupola, twin
 * symmetrical wings ending in pyramidal-roofed pavilions.
 *
 * Layout (looking south, ax = west, ay = north):
 *   ROW 0 (back):  legislative building — 6 modules wide × 1 deep
 *   ROW 1:         main facade extension + central porte-cochère
 *   ROW 2:         broad ceremonial plaza + grand approach steps
 *   ROW 3 (front): formal lawn + flagpole row + perimeter wall
 */
export function buildProvincialCapitalParts(ax: number, ay: number): VariantPart[] {
  const out: VariantPart[] = [];
  const cx = ax + 3.0;
  const cz = ay + 2.0;
  const bz = ay + 0.55;   // back-row centre (building)
  const baseY = 0.05;

  // Estate pad covers entire 6×4 footprint
  const pad = new BoxGeometry(5.95, 0.025, 3.95);
  pad.translate(cx, 0.0125, cz);
  out.push({ geom: pad, color: PC_SANDSTONE_DEEP });
  // Front lawn (row 3)
  for (const dx of [-2.0, 2.0]) {
    const lawn = new BoxGeometry(1.55, 0.018, 0.92);
    lawn.translate(cx + dx, 0.024, ay + 3.5);
    out.push({ geom: lawn, color: CH_LAWN });
  }
  // Central paved approach
  const path = new BoxGeometry(1.50, 0.022, 2.50);
  path.translate(cx, 0.028, ay + 2.30);
  out.push({ geom: path, color: CH_PATH });
  // Side lawns rows 1-2
  for (const dx of [-2.0, 2.0]) {
    const sideLawn = new BoxGeometry(1.55, 0.018, 1.75);
    sideLawn.translate(cx + dx, 0.024, ay + 2.0);
    out.push({ geom: sideLawn, color: CH_LAWN });
  }

  // Perimeter wall — heavier than the city hall, no front gate break (the
  // gate is centred and slightly raised).
  const fwL = new BoxGeometry(2.35, 0.12, 0.07);
  fwL.translate(cx - 1.80, 0.08, cz + 1.97);
  out.push({ geom: fwL, color: PC_SANDSTONE });
  const fwR = new BoxGeometry(2.35, 0.12, 0.07);
  fwR.translate(cx + 1.80, 0.08, cz + 1.97);
  out.push({ geom: fwR, color: PC_SANDSTONE });
  const backW = new BoxGeometry(5.95, 0.12, 0.07);
  backW.translate(cx, 0.08, cz - 1.97);
  out.push({ geom: backW, color: PC_SANDSTONE });
  const eW = new BoxGeometry(0.07, 0.12, 3.95);
  eW.translate(cx + 2.97, 0.08, cz);
  out.push({ geom: eW, color: PC_SANDSTONE });
  const wW = new BoxGeometry(0.07, 0.12, 3.95);
  wW.translate(cx - 2.97, 0.08, cz);
  out.push({ geom: wW, color: PC_SANDSTONE });
  // Corner posts and gate piers
  for (const [px, pz] of [[-2.97, -1.97], [2.97, -1.97], [-2.97, 1.97], [2.97, 1.97], [-0.65, 1.97], [0.65, 1.97]] as Array<[number, number]>) {
    const post = new BoxGeometry(0.12, 0.26, 0.12);
    post.translate(cx + px, 0.13, cz + pz);
    out.push({ geom: post, color: PC_SANDSTONE_SHADOW });
    const finial = new ConeGeometry(0.07, 0.10, 4);
    finial.translate(cx + px, 0.31, cz + pz);
    out.push({ geom: finial, color: PC_SANDSTONE });
  }

  // ===== MAIN BUILDING — Queens Park composition =====
  // Five-block composition: west pavilion → west wing → CENTRAL block
  // (with arched entrance + low tower + cupola) → east wing → east
  // pavilion. The pavilions have Queens Park's distinctive tall
  // pyramidal slate roofs.
  const bdpth = 0.95;
  const bldgZ = bz - 0.08;
  const bldgFrontZ = bldgZ + bdpth / 2;

  // --- WEST END PAVILION ---
  // Sandstone block 0.95 wide × 0.95 tall + tall pyramidal roof.
  const wPavBody = new BoxGeometry(0.95, 0.95, bdpth);
  wPavBody.translate(cx - 2.50, baseY + 0.475, bldgZ);
  out.push({ geom: wPavBody, color: PC_SANDSTONE });
  // Cornice
  const wPavCornice = new BoxGeometry(1.05, 0.06, bdpth + 0.06);
  wPavCornice.translate(cx - 2.50, baseY + 0.95 + 0.03, bldgZ);
  out.push({ geom: wPavCornice, color: PC_SANDSTONE_SHADOW });
  // Tall pyramidal roof — Queens Park's signature
  const wPavRoof = new ConeGeometry(0.62, 0.65, 4);
  wPavRoof.rotateY(Math.PI / 4);
  wPavRoof.translate(cx - 2.50, baseY + 0.95 + 0.06 + 0.325, bldgZ);
  out.push({ geom: wPavRoof, color: PC_SLATE_ROOF });
  // Roof spike finial
  const wPavSpike = new CylinderGeometry(0.018, 0.022, 0.20, 6);
  wPavSpike.translate(cx - 2.50, baseY + 0.95 + 0.06 + 0.65 + 0.10, bldgZ);
  out.push({ geom: wPavSpike, color: CH_GOLD });
  // Windows on the pavilion (3 per face)
  for (const wxOff of [-0.25, 0, 0.25]) {
    for (let story = 0; story < 2; story++) {
      const win = new BoxGeometry(0.10, 0.20, 0.014);
      win.translate(cx - 2.50 + wxOff, baseY + 0.22 + story * 0.40, bldgFrontZ + 0.007);
      out.push({ geom: win, color: CH_DARK_WINDOW });
    }
  }

  // --- WEST WING ---
  const wWingBody = new BoxGeometry(1.10, 0.80, bdpth);
  wWingBody.translate(cx - 1.45, baseY + 0.40, bldgZ);
  out.push({ geom: wWingBody, color: PC_SANDSTONE });
  // Steep gabled roof along the wing's length
  const wWingRoof = new BoxGeometry(1.15, 0.20, bdpth + 0.04);
  wWingRoof.translate(cx - 1.45, baseY + 0.90, bldgZ);
  out.push({ geom: wWingRoof, color: PC_SLATE_ROOF });
  // Roof ridge
  const wWingRidge = new ConeGeometry(0.45, 0.18, 4);
  wWingRidge.rotateY(Math.PI / 4);
  wWingRidge.scale(0.40, 1.0, 1.30);   // long ridge
  wWingRidge.translate(cx - 1.45, baseY + 1.00 + 0.09, bldgZ);
  out.push({ geom: wWingRidge, color: PC_ROOF_DARK });
  // 6 arched windows on the front face
  for (const wxOff of [-0.45, -0.27, -0.09, 0.09, 0.27, 0.45]) {
    const pilaster = new BoxGeometry(0.045, 0.65, 0.025);
    pilaster.translate(cx - 1.45 + wxOff - 0.09, baseY + 0.36, bldgFrontZ + 0.013);
    out.push({ geom: pilaster, color: PC_SANDSTONE_SHADOW });
    const win = new BoxGeometry(0.09, 0.36, 0.012);
    win.translate(cx - 1.45 + wxOff, baseY + 0.38, bldgFrontZ + 0.007);
    out.push({ geom: win, color: CH_DARK_WINDOW });
    const arch = new CylinderGeometry(0.045, 0.045, 0.012, 8, 1, false, 0, Math.PI);
    arch.rotateX(Math.PI / 2);
    arch.rotateZ(-Math.PI / 2);
    arch.translate(cx - 1.45 + wxOff, baseY + 0.57, bldgFrontZ + 0.008);
    out.push({ geom: arch, color: CH_DARK_WINDOW });
  }

  // --- CENTRAL BLOCK (the signature Queens Park face) ---
  // Wider and slightly taller than the wings. Romanesque Revival —
  // huge arched main entrance, low central tower with pyramidal copper
  // cupola.
  const centerH = 1.10;
  const ccBody = new BoxGeometry(1.55, centerH, bdpth + 0.10);
  ccBody.translate(cx, baseY + centerH / 2, bldgZ + 0.05);
  out.push({ geom: ccBody, color: PC_SANDSTONE });
  // Entablature band
  const ccBand = new BoxGeometry(1.65, 0.08, bdpth + 0.18);
  ccBand.translate(cx, baseY + centerH + 0.04, bldgZ + 0.05);
  out.push({ geom: ccBand, color: PC_SANDSTONE_SHADOW });
  // Central tower — square block rising another 0.65 above
  const tower = new BoxGeometry(0.85, 0.65, 0.85);
  tower.translate(cx, baseY + centerH + 0.08 + 0.325, bldgZ + 0.05);
  out.push({ geom: tower, color: PC_SANDSTONE });
  // Arched windows on each tower face
  for (const [tdx, tdz] of [[0, 0.42], [0.42, 0], [0, -0.42], [-0.42, 0]] as Array<[number, number]>) {
    const towerWin = new BoxGeometry(tdx !== 0 ? 0.012 : 0.16, 0.30, tdx !== 0 ? 0.16 : 0.012);
    towerWin.translate(cx + tdx, baseY + centerH + 0.08 + 0.30, bldgZ + 0.05 + tdz);
    out.push({ geom: towerWin, color: CH_DARK_WINDOW });
  }
  // Tower cornice
  const towerBand = new BoxGeometry(0.92, 0.06, 0.92);
  towerBand.translate(cx, baseY + centerH + 0.08 + 0.65 + 0.03, bldgZ + 0.05);
  out.push({ geom: towerBand, color: PC_SANDSTONE_SHADOW });
  // Pyramidal copper cupola roof
  const cupola = new ConeGeometry(0.60, 0.50, 4);
  cupola.rotateY(Math.PI / 4);
  cupola.translate(cx, baseY + centerH + 0.08 + 0.65 + 0.06 + 0.25, bldgZ + 0.05);
  out.push({ geom: cupola, color: CH_COPPER_GREEN });
  // Cupola spike + gold ball
  const cupolaSpike = new CylinderGeometry(0.012, 0.018, 0.22, 6);
  cupolaSpike.translate(cx, baseY + centerH + 0.08 + 0.65 + 0.06 + 0.50 + 0.11, bldgZ + 0.05);
  out.push({ geom: cupolaSpike, color: CH_GOLD });
  const cupolaBall = new IcosahedronGeometry(0.040, 1);
  cupolaBall.translate(cx, baseY + centerH + 0.08 + 0.65 + 0.06 + 0.50 + 0.25, bldgZ + 0.05);
  out.push({ geom: cupolaBall, color: CH_GOLD_BRIGHT });
  // Flagpole + flag
  const cupolaPole = new CylinderGeometry(0.008, 0.008, 0.30, 5);
  cupolaPole.translate(cx, baseY + centerH + 0.08 + 0.65 + 0.06 + 0.50 + 0.40, bldgZ + 0.05);
  out.push({ geom: cupolaPole, color: 0xb8b8b8 });
  const provFlag = new BoxGeometry(0.18, 0.11, 0.005);
  provFlag.translate(cx + 0.09, baseY + centerH + 0.08 + 0.65 + 0.06 + 0.50 + 0.50, bldgZ + 0.05);
  out.push({ geom: provFlag, color: CH_RED_FLAG });

  // CENTRAL GRAND ARCHED ENTRANCE — Queens Park's signature feature.
  // A very large arched portal occupying the entire centre of the front
  // face. Approximate with a sandstone arched surround framing an inset
  // dark recess.
  const grandArchFrontZ = bldgZ + bdpth / 2 + 0.06 + 0.025;
  const grandArchOpening = new BoxGeometry(0.65, 0.70, 0.025);
  grandArchOpening.translate(cx, baseY + 0.35, grandArchFrontZ - 0.012);
  out.push({ geom: grandArchOpening, color: 0x2a1f18 });   // very dark recess
  // Arched cap above the opening
  const grandArchCap = new CylinderGeometry(0.32, 0.32, 0.025, 14, 1, false, 0, Math.PI);
  grandArchCap.rotateX(Math.PI / 2);
  grandArchCap.rotateZ(-Math.PI / 2);
  grandArchCap.translate(cx, baseY + 0.70, grandArchFrontZ - 0.012);
  out.push({ geom: grandArchCap, color: 0x2a1f18 });
  // Sandstone arch surround — voussoirs
  const grandArchSurround = new CylinderGeometry(0.38, 0.38, 0.030, 14, 1, false, 0, Math.PI);
  grandArchSurround.rotateX(Math.PI / 2);
  grandArchSurround.rotateZ(-Math.PI / 2);
  grandArchSurround.translate(cx, baseY + 0.70, grandArchFrontZ + 0.005);
  out.push({ geom: grandArchSurround, color: PC_SANDSTONE_SHADOW });
  // Keystone
  const keystone = new BoxGeometry(0.10, 0.16, 0.035);
  keystone.translate(cx, baseY + 0.86, grandArchFrontZ + 0.007);
  out.push({ geom: keystone, color: PC_SANDSTONE_SHADOW });
  // Flanking columns around the arch
  for (const dx of [-0.45, 0.45]) {
    const archCol = new CylinderGeometry(0.06, 0.07, 0.85, 8);
    archCol.translate(cx + dx, baseY + 0.425, grandArchFrontZ + 0.015);
    out.push({ geom: archCol, color: PC_SANDSTONE_SHADOW });
    // Capital
    const archCapDeco = new BoxGeometry(0.15, 0.05, 0.15);
    archCapDeco.translate(cx + dx, baseY + 0.875, grandArchFrontZ + 0.015);
    out.push({ geom: archCapDeco, color: PC_SANDSTONE_DEEP });
  }
  // Provincial crest in stone above the arch keystone
  const crest = new BoxGeometry(0.28, 0.16, 0.025);
  crest.translate(cx, baseY + 1.00, grandArchFrontZ + 0.012);
  out.push({ geom: crest, color: CH_GOLD });

  // --- EAST WING (mirror of west) ---
  const eWingBody = new BoxGeometry(1.10, 0.80, bdpth);
  eWingBody.translate(cx + 1.45, baseY + 0.40, bldgZ);
  out.push({ geom: eWingBody, color: PC_SANDSTONE });
  const eWingRoof = new BoxGeometry(1.15, 0.20, bdpth + 0.04);
  eWingRoof.translate(cx + 1.45, baseY + 0.90, bldgZ);
  out.push({ geom: eWingRoof, color: PC_SLATE_ROOF });
  const eWingRidge = new ConeGeometry(0.45, 0.18, 4);
  eWingRidge.rotateY(Math.PI / 4);
  eWingRidge.scale(0.40, 1.0, 1.30);
  eWingRidge.translate(cx + 1.45, baseY + 1.00 + 0.09, bldgZ);
  out.push({ geom: eWingRidge, color: PC_ROOF_DARK });
  for (const wxOff of [-0.45, -0.27, -0.09, 0.09, 0.27, 0.45]) {
    const pilaster = new BoxGeometry(0.045, 0.65, 0.025);
    pilaster.translate(cx + 1.45 + wxOff - 0.09, baseY + 0.36, bldgFrontZ + 0.013);
    out.push({ geom: pilaster, color: PC_SANDSTONE_SHADOW });
    const win = new BoxGeometry(0.09, 0.36, 0.012);
    win.translate(cx + 1.45 + wxOff, baseY + 0.38, bldgFrontZ + 0.007);
    out.push({ geom: win, color: CH_DARK_WINDOW });
    const arch = new CylinderGeometry(0.045, 0.045, 0.012, 8, 1, false, 0, Math.PI);
    arch.rotateX(Math.PI / 2);
    arch.rotateZ(-Math.PI / 2);
    arch.translate(cx + 1.45 + wxOff, baseY + 0.57, bldgFrontZ + 0.008);
    out.push({ geom: arch, color: CH_DARK_WINDOW });
  }

  // --- EAST END PAVILION (mirror of west) ---
  const ePavBody = new BoxGeometry(0.95, 0.95, bdpth);
  ePavBody.translate(cx + 2.50, baseY + 0.475, bldgZ);
  out.push({ geom: ePavBody, color: PC_SANDSTONE });
  const ePavCornice = new BoxGeometry(1.05, 0.06, bdpth + 0.06);
  ePavCornice.translate(cx + 2.50, baseY + 0.95 + 0.03, bldgZ);
  out.push({ geom: ePavCornice, color: PC_SANDSTONE_SHADOW });
  const ePavRoof = new ConeGeometry(0.62, 0.65, 4);
  ePavRoof.rotateY(Math.PI / 4);
  ePavRoof.translate(cx + 2.50, baseY + 0.95 + 0.06 + 0.325, bldgZ);
  out.push({ geom: ePavRoof, color: PC_SLATE_ROOF });
  const ePavSpike = new CylinderGeometry(0.018, 0.022, 0.20, 6);
  ePavSpike.translate(cx + 2.50, baseY + 0.95 + 0.06 + 0.65 + 0.10, bldgZ);
  out.push({ geom: ePavSpike, color: CH_GOLD });
  for (const wxOff of [-0.25, 0, 0.25]) {
    for (let story = 0; story < 2; story++) {
      const win = new BoxGeometry(0.10, 0.20, 0.014);
      win.translate(cx + 2.50 + wxOff, baseY + 0.22 + story * 0.40, bldgFrontZ + 0.007);
      out.push({ geom: win, color: CH_DARK_WINDOW });
    }
  }

  // ===== GRAND CEREMONIAL STAIRCASE (rows 1-2) =====
  // 5 wide steps cascading from the arched entrance.
  for (let i = 0; i < 5; i++) {
    const stepW = 1.10 + i * 0.18;
    const stepZ = ay + 0.80 + i * 0.22;
    const step = new BoxGeometry(stepW, 0.06, 0.22);
    step.translate(cx, Math.max(0.040, baseY - i * 0.010 + 0.030), stepZ);
    out.push({ geom: step, color: PC_SANDSTONE_SHADOW });
  }
  // Side railings
  for (const dx of [-0.95, 0.95]) {
    const rail = new BoxGeometry(0.10, 0.34, 1.00);
    rail.translate(cx + dx, baseY + 0.17, ay + 1.30);
    out.push({ geom: rail, color: PC_SANDSTONE });
    const railCap = new ConeGeometry(0.06, 0.08, 4);
    railCap.translate(cx + dx, baseY + 0.38, ay + 1.30);
    out.push({ geom: railCap, color: CH_GOLD });
  }

  // ===== CIRCULAR FOUNTAIN IN FRONT PLAZA =====
  const pcFountainBasin = new CylinderGeometry(0.45, 0.45, 0.10, 22);
  pcFountainBasin.translate(cx, baseY + 0.05, ay + 2.85);
  out.push({ geom: pcFountainBasin, color: PC_SANDSTONE });
  const pcFountainWater = new CylinderGeometry(0.40, 0.40, 0.02, 22);
  pcFountainWater.translate(cx, baseY + 0.10, ay + 2.85);
  out.push({ geom: pcFountainWater, color: 0x6da8d6 });
  // Central tiered fountain — three levels
  const tier1 = new CylinderGeometry(0.20, 0.22, 0.10, 14);
  tier1.translate(cx, baseY + 0.16, ay + 2.85);
  out.push({ geom: tier1, color: PC_SANDSTONE_SHADOW });
  const tier2 = new CylinderGeometry(0.13, 0.15, 0.08, 12);
  tier2.translate(cx, baseY + 0.25, ay + 2.85);
  out.push({ geom: tier2, color: PC_SANDSTONE_SHADOW });
  const tier3 = new CylinderGeometry(0.07, 0.09, 0.06, 10);
  tier3.translate(cx, baseY + 0.32, ay + 2.85);
  out.push({ geom: tier3, color: PC_SANDSTONE_SHADOW });
  const jet = new ConeGeometry(0.04, 0.16, 8);
  jet.translate(cx, baseY + 0.43, ay + 2.85);
  out.push({ geom: jet, color: CH_GOLD_BRIGHT });

  // ===== FLAGPOLES (front of plaza) =====
  for (const dx of [-1.30, 0, 1.30]) {
    const flagBase = new BoxGeometry(0.20, 0.10, 0.20);
    flagBase.translate(cx + dx, baseY + 0.05, ay + 3.60);
    out.push({ geom: flagBase, color: PC_SANDSTONE_SHADOW });
    const pole = new CylinderGeometry(0.020, 0.022, 1.10, 6);
    pole.translate(cx + dx, baseY + 0.65, ay + 3.60);
    out.push({ geom: pole, color: 0xb8b8b8 });
    const fcolor = dx === 0 ? CH_RED_FLAG : (dx < 0 ? CH_BLUE_FLAG : 0xe9e9e9);
    const fF = new BoxGeometry(0.24, 0.16, 0.005);
    fF.translate(cx + dx + 0.12, baseY + 1.05, ay + 3.60);
    out.push({ geom: fF, color: fcolor });
    const flagBall = new IcosahedronGeometry(0.024, 1);
    flagBall.translate(cx + dx, baseY + 1.21, ay + 3.60);
    out.push({ geom: flagBall, color: CH_GOLD });
  }

  // ===== LAMPPOSTS along the perimeter =====
  const pcLamps: Array<[number, number]> = [
    [-2.78, -0.50], [2.78, -0.50],
    [-2.78,  0.80], [2.78,  0.80],
    [-2.78,  1.78], [2.78,  1.78]
  ];
  for (const [px, pz] of pcLamps) {
    const lampPost = new CylinderGeometry(0.022, 0.028, 0.50, 6);
    lampPost.translate(cx + px, baseY + 0.25, cz + pz);
    out.push({ geom: lampPost, color: CH_LAMPPOST });
    const lampHouse = new BoxGeometry(0.10, 0.10, 0.10);
    lampHouse.translate(cx + px, baseY + 0.55, cz + pz);
    out.push({ geom: lampHouse, color: CH_LAMPPOST });
    const bulb = new IcosahedronGeometry(0.040, 1);
    bulb.translate(cx + px, baseY + 0.55, cz + pz);
    out.push({ geom: bulb, color: CH_LAMP_BULB });
  }

  // ===== ORNAMENTAL TREES (4 corners of grounds) =====
  for (const [tx, tz] of [[-2.50, 2.30], [2.50, 2.30], [-2.50, -1.55], [2.50, -1.55]] as Array<[number, number]>) {
    const shadow = new CylinderGeometry(0.35, 0.30, 0.005, 8);
    shadow.translate(cx + tx + 0.04, 0.030, cz + tz);
    out.push({ geom: shadow, color: 0x2a3a22 });
    const trunk = new CylinderGeometry(0.055, 0.065, 0.32, 6);
    trunk.translate(cx + tx, 0.19, cz + tz);
    out.push({ geom: trunk, color: CH_TREE_TRUNK });
    const foliage = new ConeGeometry(0.32, 0.70, 8);
    foliage.translate(cx + tx, 0.70, cz + tz);
    out.push({ geom: foliage, color: CH_TREE_LEAF });
    const blob = new ConeGeometry(0.22, 0.42, 8);
    blob.translate(cx + tx + 0.08, 0.92, cz + tz - 0.08);
    out.push({ geom: blob, color: CH_TREE_LEAF });
  }

  return out;
}

/**
 * National Capital (Alpha 4.12). 7×4 footprint, Centre Block (Ottawa)
 * influence — Gothic Revival sandstone, tall central Peace-Tower-style
 * clock tower with copper-spired roof, twin symmetric wings ending in
 * smaller flag towers, round Library of Parliament element behind, broad
 * formal grounds with eternal flame fountain.
 *
 * Tower height: deliberately capped well below skyscraper peak (~10
 * units vs skyscraper's ~15 max) so this reads as the tallest civic
 * monument but not a tower in the skyscraper sense.
 *
 * Layout (looking south):
 *   ROW 0 (back):  library round element + back wall of main building
 *   ROW 1:         main building — 7 modules wide, central PEACE TOWER
 *   ROW 2:         broad ceremonial portico + flanking porte-cochères
 *   ROW 3 (front): ceremonial plaza + eternal flame + flagpole row
 */
export function buildNationalCapitalParts(ax: number, ay: number): VariantPart[] {
  const out: VariantPart[] = [];
  const cx = ax + 3.5;
  const cz = ay + 2.0;
  const baseY = 0.05;
  const bz = ay + 1.10;    // building-row centre (row 1)
  const fz = ay + 3.20;    // front-plaza centre (row 3)

  // Estate pad (7 × 4)
  const pad = new BoxGeometry(6.95, 0.025, 3.95);
  pad.translate(cx, 0.0125, cz);
  out.push({ geom: pad, color: NC_SANDSTONE_DEEP });
  // Front lawn (row 3 outer)
  for (const dx of [-2.5, 2.5]) {
    const lawn = new BoxGeometry(1.55, 0.018, 0.85);
    lawn.translate(cx + dx, 0.024, ay + 3.45);
    out.push({ geom: lawn, color: CH_LAWN });
  }
  // Ceremonial central paved approach
  const path = new BoxGeometry(1.80, 0.022, 2.60);
  path.translate(cx, 0.028, ay + 2.40);
  out.push({ geom: path, color: CH_PATH });
  // Side lawns rows 1-2
  for (const dx of [-2.5, 2.5]) {
    const sideLawn = new BoxGeometry(1.55, 0.018, 1.65);
    sideLawn.translate(cx + dx, 0.024, ay + 2.10);
    out.push({ geom: sideLawn, color: CH_LAWN });
  }

  // Wrought-iron perimeter fence — taller and statelier than the
  // others. Front gate opening with monumental posts.
  for (const [px, pz, w, d] of [
    [-2.20, 1.97, 2.40, 0.06], [2.20, 1.97, 2.40, 0.06],   // front L/R of gate
    [0, -1.97, 6.95, 0.06],                                  // back wall
  ] as Array<[number, number, number, number]>) {
    const seg = new BoxGeometry(w, 0.16, d);
    seg.translate(cx + px, 0.10, cz + pz);
    out.push({ geom: seg, color: NC_SANDSTONE_SHADOW });
  }
  // East + west walls
  for (const px of [-3.47, 3.47]) {
    const seg = new BoxGeometry(0.07, 0.16, 3.95);
    seg.translate(cx + px, 0.10, cz);
    out.push({ geom: seg, color: NC_SANDSTONE_SHADOW });
  }
  // Eight perimeter posts (corners + gate piers + mid-side)
  for (const [px, pz] of [
    [-3.47, -1.97], [3.47, -1.97], [-3.47, 1.97], [3.47, 1.97],
    [-1.00, 1.97], [1.00, 1.97],
    [-3.47, 0.00], [3.47, 0.00]
  ] as Array<[number, number]>) {
    const post = new BoxGeometry(0.14, 0.32, 0.14);
    post.translate(cx + px, 0.16, cz + pz);
    out.push({ geom: post, color: NC_SANDSTONE_SHADOW });
    // Gold-cap finial
    const finialCap = new ConeGeometry(0.08, 0.14, 4);
    finialCap.translate(cx + px, 0.39, cz + pz);
    out.push({ geom: finialCap, color: CH_GOLD });
  }

  // ===== LIBRARY OF PARLIAMENT (round element behind the main building) =====
  // Sits in the back row (row 0), centred. Round drum + copper-green
  // conical roof.
  const libZ = ay + 0.40;
  const libDrum = new CylinderGeometry(0.55, 0.55, 0.95, 18);
  libDrum.translate(cx, baseY + 0.475, libZ);
  out.push({ geom: libDrum, color: NC_SANDSTONE });
  // Buttress ribs around the drum (Gothic flying buttresses simplified)
  for (let i = 0; i < 16; i++) {
    const angle = (i / 16) * Math.PI * 2;
    const buttress = new BoxGeometry(0.05, 0.85, 0.10);
    buttress.translate(
      cx + Math.cos(angle) * 0.58,
      baseY + 0.425,
      libZ + Math.sin(angle) * 0.58
    );
    buttress.rotateY(angle + Math.PI / 2);
    out.push({ geom: buttress, color: NC_SANDSTONE_SHADOW });
  }
  // Pointed-arch windows on the drum (8)
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const win = new BoxGeometry(0.08, 0.40, 0.014);
    win.translate(
      cx + Math.cos(angle) * 0.54,
      baseY + 0.40,
      libZ + Math.sin(angle) * 0.54
    );
    win.rotateY(angle + Math.PI / 2);
    out.push({ geom: win, color: CH_DARK_WINDOW });
  }
  // Drum cornice
  const libCornice = new CylinderGeometry(0.60, 0.60, 0.06, 18);
  libCornice.translate(cx, baseY + 0.95 + 0.03, libZ);
  out.push({ geom: libCornice, color: NC_SANDSTONE_DEEP });
  // Conical copper roof
  const libRoof = new ConeGeometry(0.62, 0.55, 18);
  libRoof.translate(cx, baseY + 0.95 + 0.06 + 0.275, libZ);
  out.push({ geom: libRoof, color: NC_COPPER_ROOF });
  // Roof ribs
  for (let i = 0; i < 12; i++) {
    const angle = (i / 12) * Math.PI * 2;
    const rib = new BoxGeometry(0.015, 0.56, 0.015);
    rib.translate(
      cx + Math.cos(angle) * 0.28,
      baseY + 0.95 + 0.06 + 0.275,
      libZ + Math.sin(angle) * 0.28
    );
    rib.rotateY(angle);
    out.push({ geom: rib, color: NC_COPPER_DEEP });
  }
  // Library finial
  const libFinial = new CylinderGeometry(0.012, 0.018, 0.22, 6);
  libFinial.translate(cx, baseY + 0.95 + 0.06 + 0.55 + 0.11, libZ);
  out.push({ geom: libFinial, color: CH_GOLD });
  const libBall = new IcosahedronGeometry(0.040, 1);
  libBall.translate(cx, baseY + 0.95 + 0.06 + 0.55 + 0.26, libZ);
  out.push({ geom: libBall, color: CH_GOLD_BRIGHT });

  // ===== MAIN BUILDING (row 1) — 7 modules wide =====
  const bdpth = 0.85;
  const bldgZ = bz - 0.05;
  const bldgFrontZ = bldgZ + bdpth / 2;

  // --- WEST FLAG TOWER (block 1) ---
  const wTowerH = 1.40;
  const wTower = new BoxGeometry(0.85, wTowerH, bdpth + 0.05);
  wTower.translate(cx - 3.05, baseY + wTowerH / 2, bldgZ);
  out.push({ geom: wTower, color: NC_SANDSTONE });
  // Tower cornice
  const wTowerCornice = new BoxGeometry(0.95, 0.07, bdpth + 0.15);
  wTowerCornice.translate(cx - 3.05, baseY + wTowerH + 0.035, bldgZ);
  out.push({ geom: wTowerCornice, color: NC_SANDSTONE_DEEP });
  // Tower roof — copper pyramid
  const wTowerRoof = new ConeGeometry(0.50, 0.40, 4);
  wTowerRoof.rotateY(Math.PI / 4);
  wTowerRoof.translate(cx - 3.05, baseY + wTowerH + 0.07 + 0.20, bldgZ);
  out.push({ geom: wTowerRoof, color: NC_COPPER_ROOF });
  // Tower finial + flag
  const wTowerSpike = new CylinderGeometry(0.012, 0.018, 0.18, 6);
  wTowerSpike.translate(cx - 3.05, baseY + wTowerH + 0.07 + 0.40 + 0.09, bldgZ);
  out.push({ geom: wTowerSpike, color: CH_GOLD });
  const wTowerBall = new IcosahedronGeometry(0.030, 1);
  wTowerBall.translate(cx - 3.05, baseY + wTowerH + 0.07 + 0.40 + 0.20, bldgZ);
  out.push({ geom: wTowerBall, color: CH_GOLD_BRIGHT });
  // Tower windows — 2 narrow Gothic on the front face per storey
  for (let story = 0; story < 4; story++) {
    for (const tdx of [-0.18, 0.18]) {
      const win = new BoxGeometry(0.10, 0.22, 0.014);
      win.translate(cx - 3.05 + tdx, baseY + 0.20 + story * 0.32, bldgFrontZ + 0.007);
      out.push({ geom: win, color: CH_DARK_WINDOW });
    }
  }

  // --- WEST WING (block 2) — long Gothic Revival hall ---
  const wWingH = 1.05;
  const wWingBody = new BoxGeometry(1.20, wWingH, bdpth);
  wWingBody.translate(cx - 2.00, baseY + wWingH / 2, bldgZ);
  out.push({ geom: wWingBody, color: NC_SANDSTONE });
  // Steep slate roof (copper bands)
  const wWingRoof = new BoxGeometry(1.25, 0.22, bdpth + 0.06);
  wWingRoof.translate(cx - 2.00, baseY + wWingH + 0.11, bldgZ);
  out.push({ geom: wWingRoof, color: NC_COPPER_DEEP });
  // Ridge
  const wWingRidge = new ConeGeometry(0.45, 0.20, 4);
  wWingRidge.rotateY(Math.PI / 4);
  wWingRidge.scale(0.40, 1.0, 1.40);
  wWingRidge.translate(cx - 2.00, baseY + wWingH + 0.22 + 0.10, bldgZ);
  out.push({ geom: wWingRidge, color: NC_COPPER_ROOF });
  // 4 pointed-arch windows on the front face
  for (const wxOff of [-0.42, -0.14, 0.14, 0.42]) {
    const pilaster = new BoxGeometry(0.05, 0.85, 0.025);
    pilaster.translate(cx - 2.00 + wxOff - 0.14, baseY + 0.45, bldgFrontZ + 0.013);
    out.push({ geom: pilaster, color: NC_SANDSTONE_SHADOW });
    const win = new BoxGeometry(0.13, 0.50, 0.012);
    win.translate(cx - 2.00 + wxOff, baseY + 0.48, bldgFrontZ + 0.007);
    out.push({ geom: win, color: CH_DARK_WINDOW });
    // Pointed Gothic arch top — small cone rotated to point up
    const arch = new ConeGeometry(0.07, 0.16, 4);
    arch.rotateY(Math.PI / 4);
    arch.scale(1.0, 1.0, 0.18);
    arch.translate(cx - 2.00 + wxOff, baseY + 0.80, bldgFrontZ + 0.012);
    out.push({ geom: arch, color: NC_SANDSTONE_SHADOW });
  }

  // --- CENTRAL BLOCK + PEACE TOWER (block 3) — the apex feature ---
  const ccH = 1.30;
  const ccBody = new BoxGeometry(1.20, ccH, bdpth + 0.18);
  ccBody.translate(cx, baseY + ccH / 2, bldgZ + 0.09);
  out.push({ geom: ccBody, color: NC_SANDSTONE });
  // Entablature
  const ccBand = new BoxGeometry(1.32, 0.10, bdpth + 0.28);
  ccBand.translate(cx, baseY + ccH + 0.05, bldgZ + 0.09);
  out.push({ geom: ccBand, color: NC_SANDSTONE_DEEP });

  // PEACE TOWER — the centrepiece. Tall narrow stone tower with clock
  // faces near the top, copper-green stepped roof, tall slender spire.
  // Total height: 9.5 units (well below skyscraper peak).
  const ptBaseY = baseY + ccH + 0.10;
  const ptLevel1H = 1.60;
  const ptLevel1 = new BoxGeometry(0.78, ptLevel1H, 0.78);
  ptLevel1.translate(cx, ptBaseY + ptLevel1H / 2, bldgZ + 0.09);
  out.push({ geom: ptLevel1, color: NC_SANDSTONE });
  // Pointed-arch windows on each face of level 1
  for (let face = 0; face < 4; face++) {
    const fx = Math.cos(face * Math.PI / 2) * 0.40;
    const fz = Math.sin(face * Math.PI / 2) * 0.40;
    for (let story = 0; story < 3; story++) {
      const win = new BoxGeometry(face % 2 === 0 ? 0.10 : 0.012, 0.28, face % 2 === 0 ? 0.012 : 0.10);
      win.translate(cx + fx, ptBaseY + 0.30 + story * 0.40, bldgZ + 0.09 + fz);
      out.push({ geom: win, color: CH_DARK_WINDOW });
    }
  }
  // Setback band
  const ptBand1 = new BoxGeometry(0.85, 0.08, 0.85);
  ptBand1.translate(cx, ptBaseY + ptLevel1H + 0.04, bldgZ + 0.09);
  out.push({ geom: ptBand1, color: NC_SANDSTONE_DEEP });
  // Level 2 — clock storey
  const ptClockH = 0.75;
  const ptClock = new BoxGeometry(0.85, ptClockH, 0.85);
  ptClock.translate(cx, ptBaseY + ptLevel1H + 0.08 + ptClockH / 2, bldgZ + 0.09);
  out.push({ geom: ptClock, color: NC_SANDSTONE });
  // Clock faces (N/E/S/W)
  for (const [fx, fz] of [[0, 0.43], [0.43, 0], [0, -0.43], [-0.43, 0]] as Array<[number, number]>) {
    const clockFace = new CylinderGeometry(0.20, 0.20, 0.012, 18);
    clockFace.rotateX(Math.PI / 2);
    if (fx !== 0) clockFace.rotateY(Math.PI / 2);
    clockFace.translate(cx + fx, ptBaseY + ptLevel1H + 0.08 + ptClockH / 2, bldgZ + 0.09 + fz);
    out.push({ geom: clockFace, color: 0xf3ead0 });
    // Roman numeral marks (4 small dots at 12/3/6/9)
    for (let k = 0; k < 4; k++) {
      const ka = (k / 4) * Math.PI * 2 + Math.PI / 4;
      const mark = new BoxGeometry(0.018, 0.018, 0.003);
      const mx = Math.cos(ka) * 0.16;
      const mz = Math.sin(ka) * 0.16;
      if (fx !== 0) {
        mark.rotateY(Math.PI / 2);
        mark.translate(cx + fx + (fx > 0 ? 0.005 : -0.005), ptBaseY + ptLevel1H + 0.08 + ptClockH / 2 + mz, bldgZ + 0.09 + mx);
      } else {
        mark.translate(cx + mx, ptBaseY + ptLevel1H + 0.08 + ptClockH / 2 + mz, bldgZ + 0.09 + fz + (fz > 0 ? 0.005 : -0.005));
      }
      out.push({ geom: mark, color: CH_DARK_WINDOW });
    }
    // Hour hand
    const hourHand = new BoxGeometry(0.075, 0.012, 0.004);
    if (fx !== 0) hourHand.rotateY(Math.PI / 2);
    if (fx !== 0) hourHand.translate(cx + fx + (fx > 0 ? 0.006 : -0.006), ptBaseY + ptLevel1H + 0.08 + ptClockH / 2, bldgZ + 0.09 + fz);
    else hourHand.translate(cx + fx, ptBaseY + ptLevel1H + 0.08 + ptClockH / 2, bldgZ + 0.09 + fz + (fz > 0 ? 0.006 : -0.006));
    out.push({ geom: hourHand, color: CH_DARK_WINDOW });
    // Minute hand (vertical)
    const minHand = new BoxGeometry(0.012, 0.13, 0.004);
    if (fx !== 0) {
      minHand.rotateY(Math.PI / 2);
      minHand.translate(cx + fx + (fx > 0 ? 0.006 : -0.006), ptBaseY + ptLevel1H + 0.08 + ptClockH / 2 + 0.05, bldgZ + 0.09 + fz);
    } else {
      minHand.translate(cx + fx, ptBaseY + ptLevel1H + 0.08 + ptClockH / 2 + 0.05, bldgZ + 0.09 + fz + (fz > 0 ? 0.006 : -0.006));
    }
    out.push({ geom: minHand, color: CH_DARK_WINDOW });
  }
  // Cornice above clock
  const ptClockCornice = new BoxGeometry(0.92, 0.07, 0.92);
  ptClockCornice.translate(cx, ptBaseY + ptLevel1H + 0.08 + ptClockH + 0.035, bldgZ + 0.09);
  out.push({ geom: ptClockCornice, color: NC_SANDSTONE_DEEP });
  // Stepped copper-green roof — first level
  const ptRoof1 = new ConeGeometry(0.50, 0.32, 4);
  ptRoof1.rotateY(Math.PI / 4);
  ptRoof1.translate(cx, ptBaseY + ptLevel1H + 0.08 + ptClockH + 0.07 + 0.16, bldgZ + 0.09);
  out.push({ geom: ptRoof1, color: NC_COPPER_ROOF });
  // Open lantern stage
  const ptLantern = new BoxGeometry(0.42, 0.30, 0.42);
  ptLantern.translate(cx, ptBaseY + ptLevel1H + 0.08 + ptClockH + 0.07 + 0.32 + 0.15, bldgZ + 0.09);
  out.push({ geom: ptLantern, color: NC_SANDSTONE_SHADOW });
  // Lantern roof
  const ptRoof2 = new ConeGeometry(0.32, 0.32, 4);
  ptRoof2.rotateY(Math.PI / 4);
  ptRoof2.translate(cx, ptBaseY + ptLevel1H + 0.08 + ptClockH + 0.07 + 0.32 + 0.30 + 0.16, bldgZ + 0.09);
  out.push({ geom: ptRoof2, color: NC_COPPER_ROOF });
  // Tall spire — narrow pointed cone on top of the lantern roof
  const ptSpire = new ConeGeometry(0.10, 1.20, 8);
  ptSpire.translate(cx, ptBaseY + ptLevel1H + 0.08 + ptClockH + 0.07 + 0.32 + 0.30 + 0.32 + 0.60, bldgZ + 0.09);
  out.push({ geom: ptSpire, color: NC_COPPER_DEEP });
  // Spire ribs
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2;
    const rib = new BoxGeometry(0.018, 1.22, 0.018);
    rib.translate(
      cx + Math.cos(angle) * 0.05,
      ptBaseY + ptLevel1H + 0.08 + ptClockH + 0.07 + 0.32 + 0.30 + 0.32 + 0.60,
      bldgZ + 0.09 + Math.sin(angle) * 0.05
    );
    rib.rotateY(angle);
    out.push({ geom: rib, color: NC_COPPER_ROOF });
  }
  // Apex flagpole + flag (tower peak — well below skyscraper height)
  const peakY = ptBaseY + ptLevel1H + 0.08 + ptClockH + 0.07 + 0.32 + 0.30 + 0.32 + 1.20;
  const ptFlagpole = new CylinderGeometry(0.012, 0.012, 0.40, 5);
  ptFlagpole.translate(cx, peakY + 0.20, bldgZ + 0.09);
  out.push({ geom: ptFlagpole, color: 0xc8c8c8 });
  const ptFlag = new BoxGeometry(0.22, 0.14, 0.005);
  ptFlag.translate(cx + 0.11, peakY + 0.32, bldgZ + 0.09);
  out.push({ geom: ptFlag, color: CH_RED_FLAG });

  // Grand arched entrance under the tower
  const grandEntZ = bldgZ + bdpth / 2 + 0.18 + 0.025;
  const grandEntOpening = new BoxGeometry(0.55, 0.85, 0.025);
  grandEntOpening.translate(cx, baseY + 0.425, grandEntZ - 0.012);
  out.push({ geom: grandEntOpening, color: 0x2a1f18 });
  // Pointed Gothic arch above
  const gothicArchTop = new ConeGeometry(0.30, 0.36, 4);
  gothicArchTop.rotateY(Math.PI / 4);
  gothicArchTop.scale(1.0, 1.0, 0.20);
  gothicArchTop.translate(cx, baseY + 0.85 + 0.18, grandEntZ - 0.010);
  out.push({ geom: gothicArchTop, color: 0x2a1f18 });
  // Sandstone arch surround
  const gothicArchSurround = new ConeGeometry(0.36, 0.42, 4);
  gothicArchSurround.rotateY(Math.PI / 4);
  gothicArchSurround.scale(1.0, 1.0, 0.22);
  gothicArchSurround.translate(cx, baseY + 0.85 + 0.21, grandEntZ + 0.005);
  out.push({ geom: gothicArchSurround, color: NC_SANDSTONE_DEEP });
  // Red door
  const ncDoor = new BoxGeometry(0.45, 0.70, 0.018);
  ncDoor.translate(cx, baseY + 0.36, grandEntZ - 0.005);
  out.push({ geom: ncDoor, color: NC_RED_DOOR });

  // --- EAST WING (mirror of west) ---
  const eWingBody = new BoxGeometry(1.20, wWingH, bdpth);
  eWingBody.translate(cx + 2.00, baseY + wWingH / 2, bldgZ);
  out.push({ geom: eWingBody, color: NC_SANDSTONE });
  const eWingRoof = new BoxGeometry(1.25, 0.22, bdpth + 0.06);
  eWingRoof.translate(cx + 2.00, baseY + wWingH + 0.11, bldgZ);
  out.push({ geom: eWingRoof, color: NC_COPPER_DEEP });
  const eWingRidge = new ConeGeometry(0.45, 0.20, 4);
  eWingRidge.rotateY(Math.PI / 4);
  eWingRidge.scale(0.40, 1.0, 1.40);
  eWingRidge.translate(cx + 2.00, baseY + wWingH + 0.22 + 0.10, bldgZ);
  out.push({ geom: eWingRidge, color: NC_COPPER_ROOF });
  for (const wxOff of [-0.42, -0.14, 0.14, 0.42]) {
    const pilaster = new BoxGeometry(0.05, 0.85, 0.025);
    pilaster.translate(cx + 2.00 + wxOff - 0.14, baseY + 0.45, bldgFrontZ + 0.013);
    out.push({ geom: pilaster, color: NC_SANDSTONE_SHADOW });
    const win = new BoxGeometry(0.13, 0.50, 0.012);
    win.translate(cx + 2.00 + wxOff, baseY + 0.48, bldgFrontZ + 0.007);
    out.push({ geom: win, color: CH_DARK_WINDOW });
    const arch = new ConeGeometry(0.07, 0.16, 4);
    arch.rotateY(Math.PI / 4);
    arch.scale(1.0, 1.0, 0.18);
    arch.translate(cx + 2.00 + wxOff, baseY + 0.80, bldgFrontZ + 0.012);
    out.push({ geom: arch, color: NC_SANDSTONE_SHADOW });
  }

  // --- EAST FLAG TOWER (mirror of west) ---
  const eTower = new BoxGeometry(0.85, wTowerH, bdpth + 0.05);
  eTower.translate(cx + 3.05, baseY + wTowerH / 2, bldgZ);
  out.push({ geom: eTower, color: NC_SANDSTONE });
  const eTowerCornice = new BoxGeometry(0.95, 0.07, bdpth + 0.15);
  eTowerCornice.translate(cx + 3.05, baseY + wTowerH + 0.035, bldgZ);
  out.push({ geom: eTowerCornice, color: NC_SANDSTONE_DEEP });
  const eTowerRoof = new ConeGeometry(0.50, 0.40, 4);
  eTowerRoof.rotateY(Math.PI / 4);
  eTowerRoof.translate(cx + 3.05, baseY + wTowerH + 0.07 + 0.20, bldgZ);
  out.push({ geom: eTowerRoof, color: NC_COPPER_ROOF });
  const eTowerSpike = new CylinderGeometry(0.012, 0.018, 0.18, 6);
  eTowerSpike.translate(cx + 3.05, baseY + wTowerH + 0.07 + 0.40 + 0.09, bldgZ);
  out.push({ geom: eTowerSpike, color: CH_GOLD });
  const eTowerBall = new IcosahedronGeometry(0.030, 1);
  eTowerBall.translate(cx + 3.05, baseY + wTowerH + 0.07 + 0.40 + 0.20, bldgZ);
  out.push({ geom: eTowerBall, color: CH_GOLD_BRIGHT });
  for (let story = 0; story < 4; story++) {
    for (const tdx of [-0.18, 0.18]) {
      const win = new BoxGeometry(0.10, 0.22, 0.014);
      win.translate(cx + 3.05 + tdx, baseY + 0.20 + story * 0.32, bldgFrontZ + 0.007);
      out.push({ geom: win, color: CH_DARK_WINDOW });
    }
  }

  // ===== GRAND CEREMONIAL STAIRCASE =====
  for (let i = 0; i < 6; i++) {
    const stepW = 1.30 + i * 0.22;
    const stepZ = ay + 1.55 + i * 0.20;
    const step = new BoxGeometry(stepW, 0.06, 0.20);
    step.translate(cx, Math.max(0.040, baseY - i * 0.010 + 0.030), stepZ);
    out.push({ geom: step, color: NC_SANDSTONE_SHADOW });
  }
  // Side railings — granite balustrade
  for (const dx of [-1.05, 1.05]) {
    const rail = new BoxGeometry(0.12, 0.40, 1.10);
    rail.translate(cx + dx, baseY + 0.20, ay + 2.10);
    out.push({ geom: rail, color: NC_SANDSTONE });
    const railCap = new ConeGeometry(0.07, 0.10, 4);
    railCap.translate(cx + dx, baseY + 0.45, ay + 2.10);
    out.push({ geom: railCap, color: CH_GOLD });
  }

  // ===== ETERNAL FLAME FOUNTAIN (front plaza) =====
  // Octagonal pool with perpetual orange flame in centre
  const flameBasin = new CylinderGeometry(0.52, 0.55, 0.12, 8);
  flameBasin.translate(cx, baseY + 0.06, fz);
  out.push({ geom: flameBasin, color: NC_SANDSTONE });
  const flameWater = new CylinderGeometry(0.46, 0.46, 0.025, 8);
  flameWater.translate(cx, baseY + 0.12, fz);
  out.push({ geom: flameWater, color: 0x6da8d6 });
  // Central pedestal
  const flamePed = new CylinderGeometry(0.12, 0.16, 0.16, 8);
  flamePed.translate(cx, baseY + 0.20, fz);
  out.push({ geom: flamePed, color: NC_SANDSTONE_DEEP });
  // Bronze flame holder
  const flameHolder = new BoxGeometry(0.10, 0.06, 0.10);
  flameHolder.translate(cx, baseY + 0.30, fz);
  out.push({ geom: flameHolder, color: CH_BRONZE });
  // Flame — twin cones, warm orange
  const flame1 = new ConeGeometry(0.06, 0.20, 6);
  flame1.translate(cx, baseY + 0.43, fz);
  out.push({ geom: flame1, color: 0xf28a30 });
  const flame2 = new ConeGeometry(0.04, 0.12, 6);
  flame2.translate(cx + 0.015, baseY + 0.51, fz);
  out.push({ geom: flame2, color: 0xf2cd5c });

  // ===== FLAGPOLE ROW (5 across the front plaza edge) =====
  for (const dx of [-1.80, -0.90, 0, 0.90, 1.80]) {
    const flagBase = new BoxGeometry(0.22, 0.12, 0.22);
    flagBase.translate(cx + dx, baseY + 0.06, ay + 3.75);
    out.push({ geom: flagBase, color: NC_SANDSTONE_DEEP });
    const pole = new CylinderGeometry(0.022, 0.024, 1.30, 6);
    pole.translate(cx + dx, baseY + 0.75, ay + 3.75);
    out.push({ geom: pole, color: 0xc8c8c8 });
    const flagF = new BoxGeometry(0.28, 0.18, 0.005);
    flagF.translate(cx + dx + 0.14, baseY + 1.27, ay + 3.75);
    const fcolor = dx === 0 ? CH_RED_FLAG : (Math.abs(dx) < 1.4 ? CH_BLUE_FLAG : 0xe9e9e9);
    out.push({ geom: flagF, color: fcolor });
    const flagBall = new IcosahedronGeometry(0.026, 1);
    flagBall.translate(cx + dx, baseY + 1.42, ay + 3.75);
    out.push({ geom: flagBall, color: CH_GOLD });
  }

  // ===== BRONZE STATUE PAIR (flanking front approach) =====
  for (const dx of [-0.85, 0.85]) {
    const sPlinth = new BoxGeometry(0.20, 0.32, 0.20);
    sPlinth.translate(cx + dx, baseY + 0.16, fz - 0.55);
    out.push({ geom: sPlinth, color: NC_SANDSTONE_DEEP });
    const sBody = new BoxGeometry(0.07, 0.22, 0.06);
    sBody.translate(cx + dx, baseY + 0.43, fz - 0.55);
    out.push({ geom: sBody, color: CH_BRONZE });
    const sHead = new IcosahedronGeometry(0.034, 1);
    sHead.translate(cx + dx, baseY + 0.58, fz - 0.55);
    out.push({ geom: sHead, color: CH_BRONZE });
    // Plaque
    const plaque = new BoxGeometry(0.14, 0.05, 0.005);
    plaque.translate(cx + dx, baseY + 0.21, fz - 0.45);
    out.push({ geom: plaque, color: CH_GOLD });
  }

  // ===== LAMPPOSTS along the perimeter (8) =====
  for (const [px, pz] of [
    [-3.20, -0.50], [3.20, -0.50],
    [-3.20,  0.70], [3.20,  0.70],
    [-3.20,  1.70], [3.20,  1.70],
    [-1.80,  1.85], [1.80,  1.85]
  ] as Array<[number, number]>) {
    const lampPost = new CylinderGeometry(0.024, 0.030, 0.55, 6);
    lampPost.translate(cx + px, baseY + 0.275, cz + pz);
    out.push({ geom: lampPost, color: CH_LAMPPOST });
    const lampHouse = new BoxGeometry(0.10, 0.10, 0.10);
    lampHouse.translate(cx + px, baseY + 0.60, cz + pz);
    out.push({ geom: lampHouse, color: CH_LAMPPOST });
    const bulb = new IcosahedronGeometry(0.042, 1);
    bulb.translate(cx + px, baseY + 0.60, cz + pz);
    out.push({ geom: bulb, color: CH_LAMP_BULB });
  }

  // ===== ORNAMENTAL TREES (corners of grounds) =====
  for (const [tx, tz] of [[-3.10, 2.20], [3.10, 2.20], [-3.10, -1.55], [3.10, -1.55]] as Array<[number, number]>) {
    const shadow = new CylinderGeometry(0.38, 0.32, 0.005, 8);
    shadow.translate(cx + tx + 0.04, 0.030, cz + tz);
    out.push({ geom: shadow, color: 0x2a3a22 });
    const trunk = new CylinderGeometry(0.06, 0.07, 0.36, 6);
    trunk.translate(cx + tx, 0.21, cz + tz);
    out.push({ geom: trunk, color: CH_TREE_TRUNK });
    const foliage = new ConeGeometry(0.34, 0.78, 8);
    foliage.translate(cx + tx, 0.74, cz + tz);
    out.push({ geom: foliage, color: CH_TREE_LEAF });
    const blob = new ConeGeometry(0.24, 0.46, 8);
    blob.translate(cx + tx + 0.10, 0.98, cz + tz - 0.10);
    out.push({ geom: blob, color: CH_TREE_LEAF });
  }

  return out;
}

/* ============================================================================
 * Cloverleaf Interchange (Alpha 4.17)
 *
 * 5 × 5 prefab interchange — two crossing roads with curved loop ramps in
 * each of the 4 quadrants. Footprint anchored at (ax, ay) (lex-smallest
 * tile). Layout sketch:
 *
 *   . . H . .         row 0: highway endpoint (north)
 *   . r H r .         row 1: loop ramps + highway through
 *   H H X H H         row 2: highway crossover (E-W endpoints + bridge centre)
 *   . r H r .         row 3: loop ramps + highway through
 *   . . H . .         row 4: highway endpoint (south)
 *
 * Where:
 *   H = highway pavement (smooth grey asphalt + yellow centre stripes)
 *   X = bridge centre (highway over highway, bridge supports beneath)
 *   r = loop ramp pavement + curved white-stripe lane markings
 *   . = grass infield (manicured medians inside each loop)
 *
 * The 4 loop ramps are visualized as 270° arcs of short straight road
 * segments. Each loop has ~12 segments tracing a circular path inside its
 * quadrant — the segmented arc reads as a smooth curve at the city's
 * usual zoom range.
 *
 * Built per-block via the existing big-build infrastructure (Alpha 4.15).
 * On completion, Game.finalizeCloverleaf populates the road graph so cars
 * can route through; this geometry is purely the visual.
 * ========================================================================= */

// Palette — cloverleaf-specific tones.
const CL_ASPHALT = 0x383c45;          // main highway pavement
const CL_ASPHALT_DARK = 0x2a2e36;     // ramp pavement (slightly darker)
const CL_YELLOW = 0xf3c648;           // highway centre stripe
const CL_WHITE = 0xf2efe5;            // ramp / lane markings
const CL_INFIELD = 0x2f6a2d;          // grass median inside each loop
const CL_INFIELD_DARK = 0x1f4d1d;     // darker green for variation
const CL_BRIDGE_RAIL = 0x9a9a9a;      // concrete bridge rail
const CL_BRIDGE_PIER = 0x7a7e84;      // bridge support pier
const CL_LIGHT_POST = 0x1c232b;       // dark wrought-iron lampposts
const CL_LIGHT_BULB = 0xfff2b8;       // warm bulb tone

/** Y-lift constant — keep cloverleaf surface slightly above road
 *  surface so it visually overlaps player-built road tiles cleanly. */
const CL_SURFACE_Y = 0.025;
const CL_BRIDGE_Y = 0.22;             // matches BRIDGE_LIFT in Renderer

export function buildCloverleafParts(ax: number, ay: number): VariantPart[] {
  const out: VariantPart[] = [];
  // Footprint centre.
  const cx = ax + 2.5;
  const cz = ay + 2.5;

  // ===== ESTATE PAD =====
  // Subtle dark pad covering the entire 5×5 footprint so the cloverleaf
  // visually reads as one continuous structure.
  const estatePad = new BoxGeometry(4.95, 0.020, 4.95);
  estatePad.translate(cx, 0.010, cz);
  out.push({ geom: estatePad, color: CL_INFIELD_DARK });

  // ===== GRASS INFIELDS — 4 quadrants =====
  // Each quadrant is a 2×2 area inside the cloverleaf with a loop ramp
  // wrapping around it. The infield is a green pad with a darker centre
  // so it reads as manicured median.
  for (const [qx, qz] of [[-1.5, -1.5], [1.5, -1.5], [-1.5, 1.5], [1.5, 1.5]] as Array<[number, number]>) {
    const infield = new BoxGeometry(1.55, 0.018, 1.55);
    infield.translate(cx + qx, 0.020, cz + qz);
    out.push({ geom: infield, color: CL_INFIELD });
    // Centre darker patch
    const centre = new BoxGeometry(0.85, 0.022, 0.85);
    centre.translate(cx + qx, 0.022, cz + qz);
    out.push({ geom: centre, color: CL_INFIELD_DARK });
    // Single small tree in each quadrant for visual interest
    const trunk = new CylinderGeometry(0.06, 0.07, 0.32, 6);
    trunk.translate(cx + qx, 0.18, cz + qz);
    out.push({ geom: trunk, color: 0x6e3e1d });
    const foliage = new ConeGeometry(0.30, 0.65, 8);
    foliage.translate(cx + qx, 0.65, cz + qz);
    out.push({ geom: foliage, color: 0x2f6a2d });
  }

  // ===== HIGHWAY THROUGH-LANES — N-S (column 2) =====
  // Wide grey asphalt strip running the full 5-tile length of the
  // cloverleaf in the centre column. Yellow double-centre stripe.
  const nsHwyLength = 5.0;
  const nsHwyWidth = 0.85;
  const nsHwy = new BoxGeometry(nsHwyWidth, 0.020, nsHwyLength);
  nsHwy.translate(cx, CL_SURFACE_Y, cz);
  out.push({ geom: nsHwy, color: CL_ASPHALT });
  // Yellow centre stripe (double-yellow for highway)
  const nsStripeL = new BoxGeometry(0.030, 0.005, nsHwyLength * 0.95);
  nsStripeL.translate(cx - 0.045, CL_SURFACE_Y + 0.012, cz);
  out.push({ geom: nsStripeL, color: CL_YELLOW });
  const nsStripeR = new BoxGeometry(0.030, 0.005, nsHwyLength * 0.95);
  nsStripeR.translate(cx + 0.045, CL_SURFACE_Y + 0.012, cz);
  out.push({ geom: nsStripeR, color: CL_YELLOW });
  // White shoulder stripes
  for (const dx of [-(nsHwyWidth / 2 - 0.020), (nsHwyWidth / 2 - 0.020)]) {
    const shoulder = new BoxGeometry(0.020, 0.005, nsHwyLength * 0.95);
    shoulder.translate(cx + dx, CL_SURFACE_Y + 0.012, cz);
    out.push({ geom: shoulder, color: CL_WHITE });
  }

  // ===== HIGHWAY THROUGH-LANES — E-W (row 2), bridged over the N-S =====
  // Same geometry but rotated, lifted to BRIDGE_Y so it crosses over
  // the N-S highway at the centre.
  const ewHwy = new BoxGeometry(nsHwyLength, 0.020, nsHwyWidth);
  ewHwy.translate(cx, CL_BRIDGE_Y, cz);
  out.push({ geom: ewHwy, color: CL_ASPHALT });
  const ewStripeT = new BoxGeometry(nsHwyLength * 0.95, 0.005, 0.030);
  ewStripeT.translate(cx, CL_BRIDGE_Y + 0.012, cz - 0.045);
  out.push({ geom: ewStripeT, color: CL_YELLOW });
  const ewStripeB = new BoxGeometry(nsHwyLength * 0.95, 0.005, 0.030);
  ewStripeB.translate(cx, CL_BRIDGE_Y + 0.012, cz + 0.045);
  out.push({ geom: ewStripeB, color: CL_YELLOW });
  for (const dz of [-(nsHwyWidth / 2 - 0.020), (nsHwyWidth / 2 - 0.020)]) {
    const shoulder = new BoxGeometry(nsHwyLength * 0.95, 0.005, 0.020);
    shoulder.translate(cx, CL_BRIDGE_Y + 0.012, cz + dz);
    out.push({ geom: shoulder, color: CL_WHITE });
  }

  // ===== BRIDGE RAILS =====
  // Concrete parapet rails along both edges of the upper E-W highway,
  // running the full bridge length. Reads as a real overpass.
  for (const dz of [-(nsHwyWidth / 2 + 0.020), (nsHwyWidth / 2 + 0.020)]) {
    const rail = new BoxGeometry(nsHwyLength * 0.92, 0.10, 0.040);
    rail.translate(cx, CL_BRIDGE_Y + 0.075, cz + dz);
    out.push({ geom: rail, color: CL_BRIDGE_RAIL });
  }

  // ===== BRIDGE PIERS =====
  // 4 stout concrete columns supporting the upper E-W highway over the
  // intersection where it crosses the N-S highway. Placed just outside
  // the N-S highway's edges so they're visually inboard but don't
  // overlap the N-S surface.
  for (const [px, pz] of [[-0.55, -0.55], [0.55, -0.55], [-0.55, 0.55], [0.55, 0.55]] as Array<[number, number]>) {
    const pier = new BoxGeometry(0.10, CL_BRIDGE_Y - CL_SURFACE_Y, 0.10);
    pier.translate(cx + px, CL_SURFACE_Y + (CL_BRIDGE_Y - CL_SURFACE_Y) / 2, cz + pz);
    out.push({ geom: pier, color: CL_BRIDGE_PIER });
  }

  // ===== LOOP RAMPS — 4 quadrants =====
  // Each loop is a 270° arc traced by ~12 short box segments. The loop
  // sits inside a 2×2 quadrant area, with radius ~1.3 from quadrant
  // centre. The arc starts at the highway-on side, sweeps around the
  // grass infield, and ends at the highway-off side.
  //
  // Quadrants (offsets from cx,cz to quadrant centre):
  //  NW: (-1.5, -1.5)  // catches N→W and E→S traffic
  //  NE: ( 1.5, -1.5)  // catches N→E and W→S traffic
  //  SW: (-1.5,  1.5)
  //  SE: ( 1.5,  1.5)
  for (const [qcx, qcz, startAngleDeg, endAngleDeg] of [
    [-1.5, -1.5, 0, 270],     // NW — from E side sweeping clockwise
    [1.5, -1.5, 90, 360],     // NE
    [-1.5, 1.5, -90, 180],    // SW
    [1.5, 1.5, 180, 450],     // SE
  ] as Array<[number, number, number, number]>) {
    const radius = 1.05;
    const segments = 12;
    const arcWidth = 0.25;     // ramp width
    for (let s = 0; s < segments; s++) {
      // Interpolate angle along the arc
      const t = s / segments;
      const tNext = (s + 1) / segments;
      const angle = (startAngleDeg + t * (endAngleDeg - startAngleDeg)) * Math.PI / 180;
      const angleNext = (startAngleDeg + tNext * (endAngleDeg - startAngleDeg)) * Math.PI / 180;
      const midAngle = (angle + angleNext) / 2;
      // Mid point of the segment
      const mx = qcx + Math.cos(midAngle) * radius;
      const mz = qcz + Math.sin(midAngle) * radius;
      // Segment length = chord between angle and angleNext
      const segLen = 2 * radius * Math.sin((angleNext - angle) / 2) + 0.02;
      // Tangent direction perpendicular to radius — segment box rotates
      // so its long axis points along the tangent.
      const tangentAngle = midAngle + Math.PI / 2;
      const seg = new BoxGeometry(segLen, 0.020, arcWidth);
      seg.rotateY(tangentAngle);
      seg.translate(cx + mx, CL_SURFACE_Y, cz + mz);
      out.push({ geom: seg, color: CL_ASPHALT_DARK });
      // White shoulder stripe on the OUTER edge of the curve (radial-out)
      const stripeOffsetR = radius + arcWidth / 2 - 0.020;
      const sx = qcx + Math.cos(midAngle) * stripeOffsetR;
      const sz = qcz + Math.sin(midAngle) * stripeOffsetR;
      const stripe = new BoxGeometry(segLen * 0.85, 0.005, 0.020);
      stripe.rotateY(tangentAngle);
      stripe.translate(cx + sx, CL_SURFACE_Y + 0.013, cz + sz);
      out.push({ geom: stripe, color: CL_WHITE });
    }
  }

  // ===== LAMPPOSTS — 4 along the highway shoulders =====
  // Tall lampposts at the perimeter shoulders so the interchange reads
  // as lit/active even at day. Two on each long axis (N-S + E-W).
  for (const [px, pz] of [
    [-(nsHwyWidth / 2 + 0.10), -2.20], [(nsHwyWidth / 2 + 0.10), -2.20],
    [-(nsHwyWidth / 2 + 0.10), 2.20], [(nsHwyWidth / 2 + 0.10), 2.20],
    [-2.20, -(nsHwyWidth / 2 + 0.10)], [-2.20, (nsHwyWidth / 2 + 0.10)],
    [2.20, -(nsHwyWidth / 2 + 0.10)], [2.20, (nsHwyWidth / 2 + 0.10)],
  ] as Array<[number, number]>) {
    const post = new CylinderGeometry(0.020, 0.025, 0.55, 6);
    post.translate(cx + px, CL_SURFACE_Y + 0.275, cz + pz);
    out.push({ geom: post, color: CL_LIGHT_POST });
    const housing = new BoxGeometry(0.08, 0.06, 0.08);
    housing.translate(cx + px, CL_SURFACE_Y + 0.58, cz + pz);
    out.push({ geom: housing, color: CL_LIGHT_POST });
    const bulb = new IcosahedronGeometry(0.038, 1);
    bulb.translate(cx + px, CL_SURFACE_Y + 0.58, cz + pz);
    out.push({ geom: bulb, color: CL_LIGHT_BULB });
  }

  return out;
}

/* ============================================================================
 * The Grand Stadium (Beta 1.10)
 *
 * A multi-block showpiece sports stadium — 5 wide × 4 deep elliptical bowl.
 * Green pitch in the centre with white markings; raked seating tiers all the
 * way around in alternating section colours (the "crowd"); a cantilevered
 * white roof canopy ring; a concrete outer facade with a front entrance gap;
 * four floodlight masts at the corners; a back-of-house scoreboard; and an
 * entrance plaza with gateway pylons + flags at the front. Same anchor-tile
 * convention as the Mayor's Mansion / capitals (lex-smallest tile is the
 * anchor; emit the whole merged composition from there).
 * ========================================================================== */

const GS_CONCRETE = 0xd2cdbf;       // facade, plaza
const GS_CONCRETE_DEEP = 0xb6af9e;  // recessed openings, risers
const GS_CONCRETE_DARK = 0x8f897a;
const GS_PITCH = 0x3f8f3a;          // playing field
const GS_PITCH_DARK = 0x368030;     // mowing stripe
const GS_LINE = 0xeef2ec;           // field markings
const GS_SEAT_A = 0x2f6fb0;         // section blue
const GS_SEAT_B = 0xd2473f;         // section red
const GS_SEAT_C = 0xe6b84a;         // section gold
const GS_SEAT_D = 0xe8ecf0;         // section white
const GS_ROOF = 0xeef1f4;           // canopy
const GS_STEEL = 0x49525f;          // masts, structure
const GS_LIGHT = 0xfff7d8;          // floodlight bulbs
const GS_GOLD = 0xeec453;
const GS_GLASS = 0x1f2c40;          // scoreboard glass

export function buildGrandStadiumParts(ax: number, ay: number): VariantPart[] {
  const out: VariantPart[] = [];
  const cx = ax + 2.5;   // 5 wide
  const cz = ay + 2;     // 4 deep
  const TWO_PI = Math.PI * 2;
  const RX = 2.35, RZ = 1.85;        // outer (facade) ellipse radii
  const FX = 1.05, FZ = 0.72;        // pitch ellipse radii
  const SEG = 28;
  const GAP_C = Math.PI / 2;         // front entrance gap centred on +Z (south)
  const GAP_HALF = 0.42;
  const inGap = (th: number): boolean => {
    let d = Math.abs(th - GAP_C);
    if (d > Math.PI) d = TWO_PI - d;
    return d < GAP_HALF;
  };
  const radialYaw = (th: number): number => Math.atan2(Math.cos(th), Math.sin(th));

  // ===== Plaza pad =====
  const pad = new BoxGeometry(4.92, 0.03, 3.92);
  pad.translate(cx, 0.015, cz);
  out.push({ geom: pad, color: GS_CONCRETE });

  // ===== Pitch (green ellipse) + markings =====
  const pitch = new CylinderGeometry(1, 1, 0.05, 36);
  pitch.scale(FX, 1, FZ);
  pitch.translate(cx, 0.055, cz);
  out.push({ geom: pitch, color: GS_PITCH });
  for (const sx of [-0.45, 0.15] as number[]) {
    const stripe = new BoxGeometry(FX * 0.5, 0.006, FZ * 1.7);
    stripe.translate(cx + sx, 0.062, cz);
    out.push({ geom: stripe, color: GS_PITCH_DARK });
  }
  const midLine = new BoxGeometry(0.05, 0.006, FZ * 1.85);
  midLine.translate(cx, 0.064, cz);
  out.push({ geom: midLine, color: GS_LINE });
  const circOuter = new CylinderGeometry(1, 1, 0.006, 20);
  circOuter.scale(0.30, 1, 0.30); circOuter.translate(cx, 0.065, cz);
  out.push({ geom: circOuter, color: GS_LINE });
  const circInner = new CylinderGeometry(1, 1, 0.008, 20);
  circInner.scale(0.24, 1, 0.24); circInner.translate(cx, 0.066, cz);
  out.push({ geom: circInner, color: GS_PITCH });

  // ===== Seating bowl — concentric stepped rings, bowl rises outward. =====
  // Each ring uses per-angle exact ellipse arc length so adjacent segments
  // fill their slots perfectly without overlap or gap — the 1.3× factor used
  // previously caused tangential z-fighting between segments of the same ring,
  // producing the patchy shading glitch. The arc-length formula:
  //   ds = sqrt((rx·sin θ)² + (rz·cos θ)²) · dθ
  // gives the tangential size each box needs to tile the ellipse exactly.
  const seatPal = [GS_SEAT_A, GS_SEAT_B, GS_SEAT_C, GS_SEAT_D];
  const STEPS = 5;
  const rIn = FX + 0.12, rOut = RX - 0.16;
  const zIn = FZ + 0.12, zOut = RZ - 0.16;
  for (let s = 0; s < STEPS; s++) {
    const f = (s + 0.5) / STEPS;
    const rxMid = rIn + (rOut - rIn) * f;
    const rzMid = zIn + (zOut - zIn) * f;
    const h = 0.13 + s * 0.13;                       // top rises outward → bowl
    const depth = Math.max((rOut - rIn) / STEPS, (zOut - zIn) / STEPS) * 1.55;
    const dTheta = TWO_PI / SEG;
    for (let i = 0; i < SEG; i++) {
      const th = i * dTheta;
      // Exact ellipse arc element — no overlap between adjacent segments.
      const w = Math.sqrt(
        Math.pow(rxMid * Math.sin(th), 2) + Math.pow(rzMid * Math.cos(th), 2)
      ) * dTheta;
      const box = new BoxGeometry(w, h, depth);
      box.rotateY(radialYaw(th));
      box.translate(cx + Math.cos(th) * rxMid, h / 2 + 0.04, cz + Math.sin(th) * rzMid);
      out.push({ geom: box, color: seatPal[(i + s) % seatPal.length] });
    }
  }

  // ===== Outer facade ring (concrete) — full ring with a front entrance gap =====
  for (let i = 0; i < SEG; i++) {
    const th = (i / SEG) * TWO_PI;
    if (inGap(th)) continue;
    const arc = (TWO_PI / SEG) * 1.22;
    const w = ((RX + RZ) / 2) * arc;
    const yaw = radialYaw(th);
    const px = cx + Math.cos(th) * RX, pz = cz + Math.sin(th) * RZ;
    const wall = new BoxGeometry(w, 1.0, 0.16);
    wall.rotateY(yaw);
    wall.translate(px, 0.50, pz);
    out.push({ geom: wall, color: GS_CONCRETE });
    const band = new BoxGeometry(w * 0.92, 0.18, 0.04);
    band.rotateY(yaw);
    band.translate(px + Math.cos(th) * 0.07, 0.40, pz + Math.sin(th) * 0.07);
    out.push({ geom: band, color: GS_CONCRETE_DEEP });
    const cap = new BoxGeometry(w, 0.08, 0.22);
    cap.rotateY(yaw);
    cap.translate(px, 1.02, pz);
    out.push({ geom: cap, color: GS_CONCRETE_DARK });
  }

  // ===== Roof canopy — flat white ring band cantilevered inward (NO tilt, so
  // it shades evenly), on slim steel struts. Open at the front gap. =====
  for (let i = 0; i < SEG; i++) {
    const th = (i / SEG) * TWO_PI;
    if (inGap(th)) continue;
    const rxR = RX - 0.28, rzR = RZ - 0.28;
    const arc = (TWO_PI / SEG) * 1.3;
    const w = ((rxR + rzR) / 2) * arc;
    const yaw = radialYaw(th);
    const slab = new BoxGeometry(w, 0.06, 0.66);
    slab.rotateY(yaw);
    slab.translate(cx + Math.cos(th) * rxR, 1.00, cz + Math.sin(th) * rzR);
    out.push({ geom: slab, color: GS_ROOF });
    const strut = new BoxGeometry(0.05, 0.30, 0.05);
    strut.translate(cx + Math.cos(th) * (RX - 0.10), 0.86, cz + Math.sin(th) * (RZ - 0.10));
    out.push({ geom: strut, color: GS_STEEL });
  }

  // ===== Floodlight masts at the four diagonal corners =====
  for (const th of [Math.PI / 4, 3 * Math.PI / 4, 5 * Math.PI / 4, 7 * Math.PI / 4]) {
    const px = cx + Math.cos(th) * (RX + 0.02), pz = cz + Math.sin(th) * (RZ + 0.02);
    const mast = new CylinderGeometry(0.05, 0.07, 1.8, 6);
    mast.translate(px, 0.90, pz);
    out.push({ geom: mast, color: GS_STEEL });
    const toC = Math.atan2(cx - px, cz - pz);
    const rig = new BoxGeometry(0.44, 0.24, 0.09);
    rig.rotateY(toC);
    rig.translate(px, 1.78, pz);
    out.push({ geom: rig, color: GS_STEEL });
    // Pale bulb panel facing the pitch (the renderer makes it glow at night).
    const panel = new BoxGeometry(0.40, 0.20, 0.03);
    panel.rotateY(toC);
    panel.translate(px + Math.cos(toC) * 0.06, 1.78, pz + Math.sin(toC) * 0.06);
    out.push({ geom: panel, color: GS_LIGHT });
  }

  // ===== Scoreboard at the back (north) on a clean gantry, facing the pitch =====
  {
    const bz = cz - (RZ - 0.30);
    for (const oxp of [-0.40, 0.40] as number[]) {
      const post = new CylinderGeometry(0.05, 0.05, 1.2, 6);
      post.translate(cx + oxp, 1.0, bz);
      out.push({ geom: post, color: GS_STEEL });
    }
    const frame = new BoxGeometry(1.04, 0.56, 0.14);
    frame.translate(cx, 1.55, bz);
    out.push({ geom: frame, color: GS_GOLD });
    const screen = new BoxGeometry(0.92, 0.44, 0.05);
    screen.translate(cx, 1.55, bz + 0.06);   // face +Z, into the bowl
    out.push({ geom: screen, color: GS_GLASS });
  }

  // ===== Entrance plaza + gateway pylons + flags (front, south) =====
  {
    const fz = cz + (RZ + 0.12);
    for (let st = 0; st < 2; st++) {
      const step = new BoxGeometry(1.7 - st * 0.22, 0.05, 0.20);
      step.translate(cx, 0.05 - st * 0.02, fz + 0.10 + st * 0.20);
      out.push({ geom: step, color: GS_CONCRETE_DEEP });
    }
    for (const oxp of [-0.80, 0.80] as number[]) {
      const pylon = new BoxGeometry(0.22, 1.1, 0.22);
      pylon.translate(cx + oxp, 0.55, fz);
      out.push({ geom: pylon, color: GS_CONCRETE });
      const cap = new BoxGeometry(0.28, 0.08, 0.28);
      cap.translate(cx + oxp, 1.14, fz);
      out.push({ geom: cap, color: GS_GOLD });
      const pole = new CylinderGeometry(0.02, 0.02, 0.55, 5);
      pole.translate(cx + oxp, 1.45, fz);
      out.push({ geom: pole, color: GS_STEEL });
      const flag = new BoxGeometry(0.012, 0.20, 0.26);
      flag.translate(cx + oxp + 0.14, 1.58, fz);
      out.push({ geom: flag, color: oxp < 0 ? GS_SEAT_A : GS_SEAT_B });
    }
  }

  return out;
}
